import React, {
	useCallback,
	useLayoutEffect,
	useRef,
	useState,
	useEffect,
	useMemo,
} from 'react';
import {
	v4 as uuidv4,
} from 'uuid';

import ListingsModal from './ListingsModal';
import ListingsUploadModal from './ListingsUploadModal';
import {
	buildParentAssignmentPayload,
} from './utils/parentAssignments';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import VultureTable from './VultureTable';

import {
	dlListingsExportAll,
	dlListingsImport,
	dlViewListingsSettings,
	dlListingsTemplate,
} from 'Vulture/DataLayer';

// Import styles
import style from './ListingsTable.module.css';
import {
	getSession,
} from 'Vulture/Session';

import {
	hasPermission,
} from 'Vulture/Permissions';
import {
	parseLandmarkID,
} from './Listings';
import {
	isIntegrationBookkeepingColumn,
} from 'Vulture/ListingColumns.js';
import {
	useListingConfig,
} from './ListingConfigContext.js';

const filterByProp = (rows, id, filterValue) => {
	return rows.filter((row) => {
		if (row.original.vals[id]) {
			const rowValue = row.original.vals[id].toLowerCase();
			return rowValue.includes(filterValue.toLowerCase());
		}
	});
};


const checkIcon = <div className={style.iconWrapper}><CustomIcon icon="check"/></div>;

const disabledSquare = (systemName = '', listingName = '') => {
	return (
		<div
			className={style.disabledSquare}
			data-test-id={`system.${systemName}-listing.${listingName}`}
		>
			<div className={style.checkboxIcon}>
				<CustomIcon icon="check"/>
			</div>
		</div>
	);
};

const activeCheckbox = (systemName = '', listingName = '') => {
	return (
		<div
			className={style.activeCheckbox}
			data-test-id={`system.${systemName}-listing.${listingName}`}
		>
			<div className={style.checkboxIcon}>
				<CustomIcon icon="check"/>
			</div>
		</div>
	);
};

const rowsPerPage = 50;

// Name shown as the assignment-modal subtitle: the listing's Name column (lowest
// listing_column.order wins, not FD insertion order), else the first data column's
// value when it's plain text — injectColumnElm fills vals with rendered cells, so an
// Image/Logo first column would otherwise put JSX in the subtitle.
export const deriveListingName = (listing, listingDataColumns) => {
	if (listing && listing.cols && listing.vals) {
		const nameColKey = Object.keys(listing.cols)
			.filter((k) => {
				return listing.cols[k].fieldType === 'Name';
			})
			.sort((a, b) => {
				return (listing.cols[a].order || 0) - (listing.cols[b].order || 0);
			})[0];
		if (nameColKey) {
			const nameColUUID = nameColKey.split('.')[1];
			const nameVal = nameColUUID ? listing.vals[nameColUUID] : null;
			if (typeof nameVal === 'string' || typeof nameVal === 'number') {
				// Always a string: both render sites gate on `{listingName && ...}`,
				// and a numeric 0 would render as a bare "0" text node.
				return String(nameVal);
			}
		}
	}
	if (listingDataColumns && listing && listing.vals) {
		const firstDataCol = listingDataColumns.find((c) => {
			return c.id !== 'checkbox' && c.id !== 'scheduling' && c.id !== 'columnToHide' && c.id !== 'assignedTo';
		});
		const fallback = firstDataCol && firstDataCol.id ? listing.vals[firstDataCol.id] : null;
		if (typeof fallback === 'string' || typeof fallback === 'number') {
			return String(fallback);
		}
	}
	return '';
};

const getListingsViewCacheKey = (accountUUID, listingConfigUUID) => {
	return `listings_table_view_${accountUUID || 'global'}_${listingConfigUUID || 'default'}`;
};

const DEFAULT_SYSTEM_COL_WIDTH_PX = 120;

const LISTING_VIEW_CACHE_VERSION = 1;

const computeDefaultHiddenCols = (settings) => {
	const keys = Object.keys(settings || {}).filter((k) => !!(settings)[k]);
	return keys.slice(3);
};

const defaultListingsViewPrefs = {
	showSystemsSection:    true,
	systemsWidthPx:        null,
	systemColWidthPx:      DEFAULT_SYSTEM_COL_WIDTH_PX,
	hiddenSystemColIds:    [],
	leftColOverrideWidths: {},
	hiddenLeftColIds:      [],
};

const AssignedToChips = ({ listing, listingGroups, listingFD, fd, listingCollectionHasListingGroupRelation, toggleGroup, rowIndex, onOpenModal, isExpanded, onToggleExpand, isAssignAllActive }) => {
	const [ expandedPropertyUUIDs, setExpandedPropertyUUIDs, ] = useState(new Set());
	const [ computedVisibleCount, setComputedVisibleCount, ] = useState(null);
	const containerRef = useRef(null);
	const chipElemsRef = useRef([]);
	const addBtnRef = useRef(null);

	const assigned = useMemo(() => {
		return listingGroups.filter((g) => listingCollectionHasListingGroupRelation(g, listing));
	}, [ listingGroups, listingCollectionHasListingGroupRelation, listing, ]);

	const propertyGroups = useMemo(() => {
		if (!fd) return [];
		const groups = [];
		Object.entries(fd).forEach(([ key, val, ]) => {
			if (!key.startsWith('system_group.')) return;
			const sgUUID = key.split('.')[1];
			const sgName = (val && val.display_name) ? val.display_name : sgUUID;
			const systemIDsInProperty = new Set(
				Object.keys(val).filter((k) => k.startsWith('system.')).map((k) => k.split('.')[1])
			);
			const assignedInProperty = assigned.filter((g) =>
				g.systemM1Keys?.some((k) => systemIDsInProperty.has(k.split('.')[1]))
			);
			if (assignedInProperty.length > 0) {
				groups.push({ sgUUID, sgName, assignedGroups: assignedInProperty, });
			}
		});
		groups.sort((a, b) => a.sgName.localeCompare(b.sgName));
		return groups;
	}, [ fd, assigned, ]);

	const groupedKeys = useMemo(() => {
		const keys = new Set();
		propertyGroups.forEach((pg) => pg.assignedGroups.forEach((g) => keys.add(g.uuid)));
		return keys;
	}, [ propertyGroups, ]);

	const ungrouped = useMemo(() => assigned.filter((g) => !groupedKeys.has(g.uuid)), [ assigned, groupedKeys, ]);

	const allTopLevelItems = useMemo(() => [
		...propertyGroups.map((pg) => ({ type: 'group', pg, })),
		...ungrouped.map((g) => ({ type: 'item', g, })),
	], [ propertyGroups, ungrouped, ]);

	const totalItems = allTopLevelItems.length;

	useLayoutEffect(() => {
		setComputedVisibleCount(null);
	}, [ totalItems, ]);

	// The Assign button is the last element in the cell, so once IT fits within the
	// collapsed height everything before it (chips + "+N more") fits too. Show all
	// items first; if the trailing button is clipped, trim one item at a time (the
	// hidden ones roll into "+N more") until it's visible again.
	useLayoutEffect(() => {
		if (isExpanded || expandedPropertyUUIDs.size > 0) return;
		const container = containerRef.current;
		const addBtn = addBtnRef.current;
		if (!container || !addBtn) return;
		const containerBottom = container.getBoundingClientRect().bottom;
		const addBtnClipped = addBtn.getBoundingClientRect().bottom > containerBottom + 1;
		const chips = chipElemsRef.current.filter(Boolean);
		if (!addBtnClipped) {
			if (computedVisibleCount === null) {
				setComputedVisibleCount(chips.length);
			}
			return;
		}
		// Jump straight to the chips that fully fit, then step down one at a time to make
		// room for the trailing buttons on the last row — bounded passes either way.
		const fitCount = chips.filter((el) => {
			return el.getBoundingClientRect().bottom <= containerBottom + 1;
		}).length;
		setComputedVisibleCount((prev) => {
			const current = prev === null ? chips.length : prev;
			return Math.max(0, Math.min(current - 1, fitCount));
		});
	}, [ computedVisibleCount, isExpanded, expandedPropertyUUIDs.size, ]);

	const isExpandedRef = useRef(isExpanded);
	isExpandedRef.current = isExpanded;

	const expandedPropertyUUIDsRef = useRef(expandedPropertyUUIDs);
	expandedPropertyUUIDsRef.current = expandedPropertyUUIDs;

	useEffect(() => {
		const container = containerRef.current;
		if (!container || typeof ResizeObserver === 'undefined') return;
		// Trimming chips changes the cell height, which would re-trigger the observer and
		// restart measurement forever — only a width change invalidates the computed fit.
		let lastWidth = null;
		const ro = new ResizeObserver((entries) => {
			const width = entries[entries.length - 1]?.contentRect?.width;
			const widthChanged = lastWidth !== null && width !== lastWidth;
			lastWidth = width;
			if (widthChanged && !isExpandedRef.current && expandedPropertyUUIDsRef.current.size === 0) setComputedVisibleCount(null);
		});
		ro.observe(container);
		return () => ro.disconnect();
	}, []);

	const removeGroup = useCallback((e, group) => {
		e.stopPropagation();
		toggleGroup(group, rowIndex);
	}, [ toggleGroup, rowIndex, ]);

	const togglePropertyExpand = useCallback((e, sgUUID) => {
		e.stopPropagation();
		setExpandedPropertyUUIDs((prev) => {
			const next = new Set(prev);
			if (next.has(sgUUID)) { next.delete(sgUUID); } else { next.add(sgUUID); }
			return next;
		});
	}, []);

	const removePropertyAssignment = useCallback((e, groups) => {
		e.stopPropagation();
		groups.forEach((g) => toggleGroup(g, rowIndex));
	}, [ toggleGroup, rowIndex, ]);

	if (isAssignAllActive) {
		return (
			<div className={style.playingOnCell} style={{ cursor: 'default', maxHeight: '72px', overflow: 'hidden' }}>
				<span className={style.assignAllMessage}>Every System at This Property</span>
			</div>
		);
	}

	if (assigned.length === 0) {
		return (
			<div ref={containerRef} className={style.playingOnCell} onClick={onOpenModal} style={{ maxHeight: '72px', overflow: 'hidden' }}>
				<div className={style.playingOnAddBtn} onClick={(e) => { e.stopPropagation(); onOpenModal(e); }}>
					<CustomIcon icon='plus' /> Assign to Systems
				</div>
			</div>
		);
	}

	const hasExpandedProperties = expandedPropertyUUIDs.size > 0;
	const measuringAll = computedVisibleCount === null;
	const effectiveVisibleCount = computedVisibleCount ?? allTopLevelItems.length;
	const shownItems = (isExpanded || measuringAll) ? allTopLevelItems : allTopLevelItems.slice(0, effectiveVisibleCount);
	const hiddenCount = (isExpanded || measuringAll) ? 0 : Math.max(0, totalItems - effectiveVisibleCount);
	const showLess = isExpanded || hasExpandedProperties;

	chipElemsRef.current = [];

	return (
		<div ref={containerRef} className={style.playingOnCell} onClick={onOpenModal} style={{ maxHeight: (isExpanded || hasExpandedProperties) ? 'none' : '72px', overflow: (isExpanded || hasExpandedProperties) ? 'visible' : 'hidden' }}>
			{shownItems.map((item, i) => {
				const chipRef = (el) => { chipElemsRef.current[i] = el; };
				if (item.type === 'group') {
					const { pg, } = item;
					const isPgExpanded = expandedPropertyUUIDs.has(pg.sgUUID);
					return (
						<div key={pg.sgUUID}>
							<div ref={chipRef} className={style.playingOnChip}>
								<span className={style.chipLabel} title={pg.sgName}>{pg.sgName}</span>
								<span className={style.chipCount} data-test-id={`listingChipCount--${pg.sgUUID}`} onClick={(e) => togglePropertyExpand(e, pg.sgUUID)}>
									{pg.assignedGroups.length}&nbsp;{isPgExpanded ? '▲' : '▼'}
								</span>
								<span className={style.chipX} onClick={(e) => removePropertyAssignment(e, pg.assignedGroups)}>×</span>
							</div>
							{isPgExpanded && (
								<div className={style.playingOnExpanded}>
									{pg.assignedGroups.map((g) => (
										<div key={g.uuid} className={style.playingOnChipSub} data-test-id={`listingPlayingOnChipSub--${g.uuid}`}>
											<span className={style.chipSubLabel} title={g.name}>{g.name}</span>
											<span className={style.chipX} data-test-id={`listingChipRemove--${g.uuid}`} onClick={(e) => removeGroup(e, g)}>×</span>
										</div>
									))}
								</div>
							)}
						</div>
					);
				}
				const { g, } = item;
				return (
					<div key={g.uuid} ref={chipRef} className={style.playingOnChip}>
						<span className={style.chipLabel} title={g.name}>{g.name}</span>
						<span className={style.chipX} onClick={(e) => removeGroup(e, g)}>×</span>
					</div>
				);
			})}
			{hiddenCount > 0 && (
				<div
					className={style.playingOnMoreBtn}
					onClick={(e) => { e.stopPropagation(); onToggleExpand && onToggleExpand(); }}
				>
					+{hiddenCount} more
				</div>
			)}
			{showLess && (
				<div
					className={style.playingOnMoreBtn}
					onClick={(e) => {
						e.stopPropagation();
						setExpandedPropertyUUIDs(new Set());
						if (isExpanded) onToggleExpand && onToggleExpand();
					}}
				>
					Show less
				</div>
			)}
			<div ref={addBtnRef} className={style.playingOnAddBtn} onClick={(e) => { e.stopPropagation(); onOpenModal(e); }}>
				<CustomIcon icon='plus' /> Assign to Systems
			</div>
		</div>
	);
};

const ParentListingChips = ({ listing, listingFD, parentsPrimaryColumnData, onOpenModal }) => {
	const containerRef = useRef(null);
	const chipElemsRef = useRef([]);
	const addBtnRef = useRef(null);
	const [ computedVisibleCount, setComputedVisibleCount, ] = useState(null);
	const [ isExpanded, setIsExpanded, ] = useState(false);

	const parents = useMemo(() => {
		const parentM1Keys = listing.parent_listings || [];
		const { parentsFirstColumnEntityKey, parentsFirstColumnAttr, } = parentsPrimaryColumnData || {};
		if (!parentsFirstColumnEntityKey || !parentsFirstColumnAttr) return [];
		return parentM1Keys
			.map((m1Key) => {
				const name = listingFD?.[m1Key]?.[parentsFirstColumnEntityKey]?.[parentsFirstColumnAttr] || '';
				return { m1Key, name, };
			})
			.filter((p) => !!p.name)
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [ listing, listingFD, parentsPrimaryColumnData, ]);

	const totalItems = parents.length;

	useLayoutEffect(() => {
		setComputedVisibleCount(null);
	}, [ totalItems, ]);

	// Trim parent chips until the trailing Assign button (the last element) fits within
	// the collapsed height; once it fits, everything before it does too.
	useLayoutEffect(() => {
		if (isExpanded) return;
		const container = containerRef.current;
		const addBtn = addBtnRef.current;
		if (!container || !addBtn) return;
		const containerBottom = container.getBoundingClientRect().bottom;
		const addBtnClipped = addBtn.getBoundingClientRect().bottom > containerBottom + 1;
		const chips = chipElemsRef.current.filter(Boolean);
		if (!addBtnClipped) {
			if (computedVisibleCount === null) {
				setComputedVisibleCount(chips.length);
			}
			return;
		}
		// Jump straight to the chips that fully fit, then step down one at a time to make
		// room for the trailing buttons on the last row — bounded passes either way.
		const fitCount = chips.filter((el) => {
			return el.getBoundingClientRect().bottom <= containerBottom + 1;
		}).length;
		setComputedVisibleCount((prev) => {
			const current = prev === null ? chips.length : prev;
			return Math.max(0, Math.min(current - 1, fitCount));
		});
	}, [ computedVisibleCount, isExpanded, ]);

	const isExpandedRef = useRef(isExpanded);
	isExpandedRef.current = isExpanded;

	useEffect(() => {
		const container = containerRef.current;
		if (!container || typeof ResizeObserver === 'undefined') return;
		// Trimming chips changes the cell height, which would re-trigger the observer and
		// restart measurement forever — only a width change invalidates the computed fit.
		let lastWidth = null;
		const ro = new ResizeObserver((entries) => {
			const width = entries[entries.length - 1]?.contentRect?.width;
			const widthChanged = lastWidth !== null && width !== lastWidth;
			lastWidth = width;
			if (widthChanged && !isExpandedRef.current) setComputedVisibleCount(null);
		});
		ro.observe(container);
		return () => ro.disconnect();
	}, []);

	if (parents.length === 0) {
		return (
			<div className={style.playingOnCell} onClick={onOpenModal}>
				<div
					className={style.playingOnAddBtn}
					onClick={(e) => {
						e.stopPropagation(); onOpenModal(e);
					}}
				>
					<CustomIcon icon='plus' /> Assign to Parent Listing(s)
				</div>
			</div>
		);
	}

	const measuringAll = computedVisibleCount === null;
	const effectiveVisibleCount = computedVisibleCount ?? parents.length;
	const shownParents = (isExpanded || measuringAll) ? parents : parents.slice(0, effectiveVisibleCount);
	const hiddenCount = (isExpanded || measuringAll) ? 0 : Math.max(0, totalItems - effectiveVisibleCount);

	chipElemsRef.current = [];

	return (
		<div ref={containerRef} className={style.playingOnCell} onClick={onOpenModal} style={{ maxHeight: isExpanded ? 'none' : '72px', overflow: isExpanded ? 'visible' : 'hidden' }}>
			{shownParents.map(({ m1Key, name, }, i) => (
				<div key={m1Key} className={style.playingOnChip} ref={(el) => { chipElemsRef.current[i] = el; }}>
					<span className={style.chipLabel} title={name}>{name.length > 20 ? `${name.slice(0, 20)}…` : name}</span>
				</div>
			))}
			{hiddenCount > 0 && (
				<div
					className={`${style.playingOnMoreBtn} ${style.playingOnMoreBtnSmall}`}
					onClick={(e) => {
						e.stopPropagation();
						setIsExpanded(true);
					}}
				>
					+{hiddenCount} more
				</div>
			)}
			{isExpanded && (
				<div
					className={`${style.playingOnMoreBtn} ${style.playingOnMoreBtnSmall}`}
					onClick={(e) => {
						e.stopPropagation();
						setIsExpanded(false);
					}}
				>
					Show less
				</div>
			)}
			<div ref={addBtnRef} className={style.playingOnAddBtn} onClick={(e) => { e.stopPropagation(); onOpenModal(e); }}>
				<CustomIcon icon='plus' /> Assign to Parent Listing(s)
			</div>
		</div>
	);
};

const ParentListingAssignmentModal = ({ listing, listingDataColumns, listingFD, parentsPrimaryColumnData, listingConfigToListingsMap, activeListingConfig, app, onClose }) => {
	const [ search, setSearch, ] = useState('');

	const listingName = useMemo(() => {
		return deriveListingName(listing, listingDataColumns);
	}, [ listing, listingDataColumns, ]);

	const parentConfigKey = `listing_config.${activeListingConfig?.parentUUID}`;
	const { parentsFirstColumnEntityKey, parentsFirstColumnAttr, } = parentsPrimaryColumnData || {};

	const allParents = useMemo(() => {
		const parentListingsMap = listingConfigToListingsMap?.[parentConfigKey] || {};
		const items = Object.keys(parentListingsMap).map((m1Key) => {
			const name = (parentsFirstColumnEntityKey && parentsFirstColumnAttr)
				? (listingFD?.[m1Key]?.[parentsFirstColumnEntityKey]?.[parentsFirstColumnAttr] || '')
				: '';
			return { m1Key, name, };
		}).filter((p) => !!p.name);
		items.sort((a, b) => a.name.localeCompare(b.name));
		return items;
	}, [ listingConfigToListingsMap, parentConfigKey, listingFD, parentsFirstColumnEntityKey, parentsFirstColumnAttr, ]);

	const childM1Key = `listing.${listing.uuid}`;

	const [ pendingParentM1Keys, setPendingParentM1Keys, ] = useState(() => {
		return (listing.parent_listings || []).slice();
	});

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return allParents;
		return allParents.filter((p) => p.name.toLowerCase().includes(q));
	}, [ allParents, search, ]);

	const isChecked = useCallback((m1Key) => pendingParentM1Keys.includes(m1Key), [ pendingParentM1Keys, ]);

	const toggle = useCallback((m1Key) => {
		setPendingParentM1Keys((prev) =>
			prev.includes(m1Key) ? prev.filter((k) => k !== m1Key) : [ ...prev, m1Key, ]
		);
	}, []);

	const totalCount = allParents.length;
	const selectedCount = pendingParentM1Keys.length;

	const save = useCallback(() => {
		const originalParents = listing.parent_listings || [];
		const toAdd = pendingParentM1Keys.filter((k) => !originalParents.includes(k));
		const toRemove = originalParents.filter((k) => !pendingParentM1Keys.includes(k));
		const keys = [
			...toAdd.map((parentM1Key) => `${parentM1Key}.${childM1Key}`),
			...toRemove.map((parentM1Key) => `${parentM1Key}.${childM1Key}`),
		];
		const vals = [
			...toAdd.map(() => true),
			...toRemove.map(() => false),
		];
		if (keys.length > 0) {
			app.setValue('listings', keys, vals, () => {
				app.saveData('listings');
			});
		}
		onClose();
	}, [ listing, pendingParentM1Keys, childM1Key, app, onClose, ]);

	return (
		<div className={style.assignmentModalOverlay} onClick={onClose}>
			<div className={style.assignmentModal} onClick={(e) => e.stopPropagation()}>
				<div className={style.assignmentModalHeader}>
					<div className={style.assignmentModalTitleGroup}>
						<span className={style.assignmentModalTitle}>Assign To Parent Listing(s)</span>
						{listingName && <span className={style.assignmentModalDescription} data-test-id='assignmentModalListingName'>{listingName}</span>}
					</div>
					<span className={style.assignmentModalClose} onClick={onClose}>×</span>
				</div>
				<div className={style.assignmentModalSearch}>
					<div className={style.assignmentModalSearchWrapper}>
						<span className={style.assignmentModalSearchIcon}><CustomIcon icon="search" /></span>
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={`Search ${totalCount} listings...`}
							autoFocus
						/>
						{selectedCount > 0 && (
							<span className={style.assignmentModalSearchCount}>{selectedCount} selected</span>
						)}
					</div>
				</div>
				<div className={style.assignmentModalList}>
					{filtered.map(({ m1Key, name, }) => (
						<div
							key={m1Key}
							className={style.assignmentModalItem}
							onClick={() => toggle(m1Key)}
						>
							<input type="checkbox" checked={isChecked(m1Key)} onChange={() => toggle(m1Key)} onClick={(e) => e.stopPropagation()} />
							<span>{name.length > 20 ? `${name.slice(0, 20)}…` : name}</span>
						</div>
					))}
					{filtered.length === 0 && <div className={style.assignmentModalEmpty}>No listings found.</div>}
				</div>
				<div className={style.assignmentModalFooter}>
					<div className="button" onClick={save}>Save</div>
					<div className="button button-outlined" onClick={onClose}>Cancel</div>
				</div>
			</div>
		</div>
	);
};

const BulkParentListingAssignmentModal = ({ listingFD, parentsPrimaryColumnData, listingConfigToListingsMap, activeListingConfig, onSave, onClose }) => {
	const [search, setSearch,] = useState('');

	const parentConfigKey = `listing_config.${activeListingConfig?.parentUUID}`;
	const { parentsFirstColumnEntityKey, parentsFirstColumnAttr, } = parentsPrimaryColumnData || {};

	const allParents = useMemo(() => {
		const parentListingsMap = listingConfigToListingsMap?.[parentConfigKey] || {};
		const items = Object.keys(parentListingsMap).map((m1Key) => {
			const name = (parentsFirstColumnEntityKey && parentsFirstColumnAttr)
				? (listingFD?.[m1Key]?.[parentsFirstColumnEntityKey]?.[parentsFirstColumnAttr] || '')
				: '';
			return { m1Key, name, };
		}).filter((p) => !!p.name);
		items.sort((a, b) => a.name.localeCompare(b.name));
		return items;
	}, [listingConfigToListingsMap, parentConfigKey, listingFD, parentsFirstColumnEntityKey, parentsFirstColumnAttr,]);

	const [pendingParentM1Keys, setPendingParentM1Keys,] = useState([]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return allParents;
		return allParents.filter((p) => p.name.toLowerCase().includes(q));
	}, [allParents, search,]);

	const isChecked = useCallback((m1Key) => pendingParentM1Keys.includes(m1Key), [pendingParentM1Keys,]);

	const toggle = useCallback((m1Key) => {
		setPendingParentM1Keys((prev) =>
			prev.includes(m1Key) ? prev.filter((k) => k !== m1Key) : [...prev, m1Key,]
		);
	}, []);

	const totalCount = allParents.length;
	const selectedCount = pendingParentM1Keys.length;

	const save = useCallback(() => {
		onSave(pendingParentM1Keys);
		onClose();
	}, [onSave, pendingParentM1Keys, onClose,]);

	return (
		<div className={style.assignmentModalOverlay} onClick={onClose}>
			<div className={style.assignmentModal} onClick={(e) => e.stopPropagation()}>
				<div className={style.assignmentModalHeader}>
					<div className={style.assignmentModalTitleGroup}>
						<span className={style.assignmentModalTitle}>Assign To Parent Listings</span>
					</div>
					<span className={style.assignmentModalClose} onClick={onClose}>×</span>
				</div>
				<div className={style.assignmentModalSearch}>
					<div className={style.assignmentModalSearchWrapper}>
						<span className={style.assignmentModalSearchIcon}><CustomIcon icon="search" /></span>
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={`Search ${totalCount} listings...`}
							autoFocus
						/>
						{selectedCount > 0 && (
							<span className={style.assignmentModalSearchCount}>{selectedCount} selected</span>
						)}
					</div>
				</div>
				<div className={style.assignmentModalActions}>
					<label className={style.assignmentModalAssignAllLabel}>
						<input
							type="checkbox"
							checked={totalCount > 0 && selectedCount === totalCount}
							ref={(node) => { if (node) node.indeterminate = selectedCount > 0 && selectedCount < totalCount; }}
							onChange={(e) => { if (e.target.checked) { setPendingParentM1Keys(allParents.map((p) => p.m1Key)); } else { setPendingParentM1Keys([]); } }}
							onClick={(e) => e.stopPropagation()}
						/>
						<span>Assign to all {totalCount} listings</span>
					</label>
					<div className="button" onClick={() => setPendingParentM1Keys([])}>Clear All</div>
				</div>
				<div className={style.assignmentModalList}>
					{filtered.map(({ m1Key, name, }) => (
						<div
							key={m1Key}
							className={style.assignmentModalItem}
							onClick={() => toggle(m1Key)}
						>
							<input type="checkbox" checked={isChecked(m1Key)} onChange={() => toggle(m1Key)} onClick={(e) => e.stopPropagation()} />
							<span>{name.length > 20 ? `${name.slice(0, 20)}…` : name}</span>
						</div>
					))}
					{filtered.length === 0 && <div className={style.assignmentModalEmpty}>No listings found.</div>}
				</div>
				<div className={style.assignmentModalFooter}>
					<div className="button" onClick={save}>Save</div>
					<div className="button button-outlined" onClick={onClose}>Cancel</div>
				</div>
			</div>
		</div>
	);
};

const ListingAssignmentModal = ({ listing, listingDataColumns, listingGroups, listingFD, fd, listingCollectionHasListingGroupRelation, bulkToggleGroupsAndSave, onClose }) => {
	const [ search, setSearch, ] = useState('');

	const listingName = useMemo(() => {
		return deriveListingName(listing, listingDataColumns);
	}, [ listing, listingDataColumns, ]);

	const [ pendingUUIDs, setPendingUUIDs, ] = useState(() => {
		return listingGroups
			.filter((g) => listingCollectionHasListingGroupRelation(g, listing))
			.map((g) => g.uuid);
	});

	const propertyGroups = useMemo(() => {
		if (!fd) return [];
		const groups = [];
		Object.entries(fd).forEach(([ key, val, ]) => {
			if (!key.startsWith('system_group.')) return;
			const sgUUID = key.split('.')[1];
			const sgName = (val && val.display_name) ? val.display_name : sgUUID;
			const systemIDsInProperty = new Set(
				Object.keys(val).filter((k) => k.startsWith('system.')).map((k) => k.split('.')[1])
			);
			const groupsInProperty = listingGroups.filter((g) =>
				g.systemM1Keys?.some((k) => systemIDsInProperty.has(k.split('.')[1]))
			);
			if (groupsInProperty.length > 0) {
				groups.push({ sgUUID, sgName, uuids: groupsInProperty.map((g) => g.uuid), });
			}
		});
		groups.sort((a, b) => a.sgName.localeCompare(b.sgName));
		return groups;
	}, [ fd, listingGroups, ]);

	const propertyGroupItems = useMemo(() => {
		if (!fd) return {};
		const result = {};
		propertyGroups.forEach((pg) => {
			const systemIDsInProperty = new Set();
			Object.entries(fd).forEach(([ key, val, ]) => {
				if (!key.startsWith(`system_group.${pg.sgUUID}`)) return;
				Object.keys(val).forEach((subKey) => {
					if (subKey.startsWith('system.')) systemIDsInProperty.add(subKey.split('.')[1]);
				});
			});
			result[pg.sgUUID] = listingGroups.filter((g) =>
				g.systemM1Keys?.some((k) => systemIDsInProperty.has(k.split('.')[1]))
			);
		});
		return result;
	}, [ fd, propertyGroups, listingGroups, ]);

	const deduped = listingGroups;

	const filteredGrouped = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return null;
		const groups = [];
		const coveredUUIDs = new Set();
		propertyGroups.forEach((pg) => {
			const pgItems = propertyGroupItems[pg.sgUUID] || [];
			const pgNameMatches = pg.sgName.toLowerCase().includes(q);
			const matchingItems = pgNameMatches
				? pgItems
				: pgItems.filter((g) => (g.name || '').toLowerCase().includes(q));
			if (matchingItems.length > 0) {
				groups.push({ pg, items: matchingItems, });
				matchingItems.forEach((g) => coveredUUIDs.add(g.uuid));
			}
		});
		const ungrouped = deduped.filter((g) => {
			if (coveredUUIDs.has(g.uuid)) return false;
			return (g.name || '').toLowerCase().includes(q);
		});
		return { groups, ungrouped, };
	}, [ search, propertyGroups, propertyGroupItems, deduped, ]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return deduped;
		if (filteredGrouped) {
			const all = [];
			filteredGrouped.groups.forEach(({ items, }) => items.forEach((g) => all.push(g)));
			filteredGrouped.ungrouped.forEach((g) => all.push(g));
			return all;
		}
		return deduped;
	}, [ deduped, search, filteredGrouped, ]);

	const isChecked = useCallback((g) => pendingUUIDs.includes(g.uuid), [ pendingUUIDs, ]);

	const toggle = useCallback((g) => {
		setPendingUUIDs((prev) =>
			prev.includes(g.uuid) ? prev.filter((id) => id !== g.uuid) : [ ...prev, g.uuid, ]
		);
	}, []);

	const assignGroup = useCallback((g) => {
		setPendingUUIDs((prev) => prev.includes(g.uuid) ? prev : [ ...prev, g.uuid, ]);
	}, []);

	const clearGroup = useCallback((g) => {
		setPendingUUIDs((prev) => prev.filter((id) => id !== g.uuid));
	}, []);

	const assignProperty = useCallback((uuids) => {
		setPendingUUIDs((prev) => { const s = new Set(prev); uuids.forEach((u) => s.add(u)); return Array.from(s); });
	}, []);

	const clearProperty = useCallback((uuids) => {
		setPendingUUIDs((prev) => { const s = new Set(prev); uuids.forEach((u) => s.delete(u)); return Array.from(s); });
	}, []);

	const totalCount = deduped.length;
	const selectedCount = useMemo(() => deduped.filter((g) => pendingUUIDs.includes(g.uuid)).length, [ deduped, pendingUUIDs, ]);
	const isAllAssigned = useMemo(() => totalCount > 0 && deduped.every((g) => pendingUUIDs.includes(g.uuid)), [ deduped, pendingUUIDs, totalCount, ]);

	const assignAllCheckboxRef = useRef(null);
	useEffect(() => {
		if (assignAllCheckboxRef.current) {
			assignAllCheckboxRef.current.indeterminate = selectedCount > 0 && !isAllAssigned;
		}
	}, [ selectedCount, isAllAssigned, ]);

	const propertyRefsMap = useRef({});
	const assignmentModalListRef = useRef(null);

	const save = useCallback(() => {
		const originalUUIDs = listingGroups
			.filter((g) => listingCollectionHasListingGroupRelation(g, listing))
			.map((g) => g.uuid);
		const toAdd = pendingUUIDs.filter((id) => !originalUUIDs.includes(id));
		const toRemove = originalUUIDs.filter((id) => !pendingUUIDs.includes(id));
		bulkToggleGroupsAndSave(listing.uuid, toAdd, toRemove, onClose);
	}, [ listing, listingGroups, listingCollectionHasListingGroupRelation, pendingUUIDs, bulkToggleGroupsAndSave, onClose, ]);

	const isSearching = search.trim().length > 0;

	return (
		<div className={style.assignmentModalOverlay} onClick={onClose}>
			<div className={style.assignmentModal} onClick={(e) => e.stopPropagation()}>
				<div className={style.assignmentModalHeader}>
					<div className={style.assignmentModalTitleGroup}>
						<span className={style.assignmentModalTitle}>Assign To Systems</span>
						{listingName && <span className={style.assignmentModalDescription} data-test-id='assignmentModalListingName'>{listingName}</span>}
					</div>
					<span className={style.assignmentModalClose} onClick={onClose}>×</span>
				</div>
				<div className={style.assignmentModalSearch}>
					<div className={style.assignmentModalSearchWrapper}>
						<span className={style.assignmentModalSearchIcon}><CustomIcon icon="search" /></span>
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={`Search ${totalCount} systems...`}
							autoFocus
						/>
						{selectedCount > 0 && (
							<span className={style.assignmentModalSearchCount}>{selectedCount} selected</span>
						)}
					</div>
				</div>
				<div className={style.assignmentModalActions}>
					<label className={style.assignmentModalAssignAllLabel}>
						<input
							type="checkbox"
							checked={isAllAssigned}
							ref={assignAllCheckboxRef}
							onChange={(e) => { if (e.target.checked) { setPendingUUIDs(deduped.map((g) => g.uuid)); } else { setPendingUUIDs([]); } }}
							onClick={(e) => e.stopPropagation()}
						/>
						<span>Assign to all {totalCount} systems</span>
					</label>
					<div className="button" onClick={() => setPendingUUIDs([])}>Clear All</div>
				</div>
				{!isSearching && propertyGroups.length > 0 && (
					<div className={style.assignmentModalQuickJump}>
						<span className={style.assignmentModalQuickJumpLabel}>Quick:</span>
						{propertyGroups.map((pg) => (
							<span
								key={pg.sgUUID}
								className={style.assignmentModalQuickJumpItem}
								onClick={() => {
									const el = propertyRefsMap.current[pg.sgUUID];
									const listEl = assignmentModalListRef.current;
									if (el && listEl) {
										listEl.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
									}
								}}
							>
								{pg.sgName}
							</span>
						))}
					</div>
				)}
				<div className={style.assignmentModalList} ref={assignmentModalListRef}>
					{isSearching ? (
						<>
							{filteredGrouped && filteredGrouped.groups.map(({ pg, items, }) => {
								const checkedCount = items.filter((g) => isChecked(g)).length;
								const isPropertyAllChecked = items.length > 0 && items.every((g) => isChecked(g));
								const isPropertySomeChecked = items.some((g) => isChecked(g));
								return (
									<div key={pg.sgUUID}>
										<div className={style.assignmentModalPropertyHeader}>
											<input
												type="checkbox"
												checked={isPropertyAllChecked}
												ref={(node) => { if (node) node.indeterminate = isPropertySomeChecked && !isPropertyAllChecked; }}
												onChange={(e) => { if (e.target.checked) assignProperty(pg.uuids); else clearProperty(pg.uuids); }}
												onClick={(e) => e.stopPropagation()}
											/>
											<span className={style.assignmentModalPropertyName}>{pg.sgName}</span>
											<span className={style.assignmentModalPropertyCount}>{checkedCount}/{items.length}</span>
											<span
												className={`${style.assignmentModalToggleLink} ${style.assignmentModalPropertyToggle}`}
												onClick={(e) => { e.stopPropagation(); isPropertyAllChecked ? clearProperty(pg.uuids) : assignProperty(pg.uuids); }}
											>
												{isPropertyAllChecked ? 'Deselect all' : 'Select all'}
											</span>
										</div>
										{items.map((g) => (
											<div
												key={g.uuid}
												className={`${style.assignmentModalItem} ${style.assignmentModalItemIndented}${g.grouped ? ` ${style.assignmentModalGroupItem}` : ''}`}
												onClick={() => toggle(g)}
											>
												<input type="checkbox" checked={isChecked(g)} onChange={() => toggle(g)} onClick={(e) => e.stopPropagation()} />
												<span className={g.grouped ? style.assignmentModalGroupLabel : ''}>{g.name || g.uuid}</span>
												{g.grouped && (
													<span
														className={style.assignmentModalToggleLink}
														onClick={(e) => { e.stopPropagation(); isChecked(g) ? clearGroup(g) : assignGroup(g); }}
													>
														{isChecked(g) ? 'Deselect all' : 'Select all'}
													</span>
												)}
											</div>
										))}
									</div>
								);
							})}
							{filteredGrouped && filteredGrouped.ungrouped.map((g) => (
								<div
									key={g.uuid}
									className={`${style.assignmentModalItem}${g.grouped ? ` ${style.assignmentModalGroupItem}` : ''}`}
									onClick={() => toggle(g)}
								>
									<input type="checkbox" checked={isChecked(g)} onChange={() => toggle(g)} onClick={(e) => e.stopPropagation()} />
									<span className={g.grouped ? style.assignmentModalGroupLabel : ''}>{g.name || g.uuid}</span>
									{g.grouped && (
										<span
											className={style.assignmentModalToggleLink}
											onClick={(e) => { e.stopPropagation(); isChecked(g) ? clearGroup(g) : assignGroup(g); }}
										>
											{isChecked(g) ? 'Deselect all' : 'Select all'}
										</span>
									)}
								</div>
							))}
							{filtered.length === 0 && <div className={style.assignmentModalEmpty}>No systems found.</div>}
						</>
					) : propertyGroups.length > 0 ? propertyGroups.map((pg) => {
						const pgItems = propertyGroupItems[pg.sgUUID] || [];
						if (pgItems.length === 0) return null;
						const checkedCount = pgItems.filter((g) => isChecked(g)).length;
						const isPropertyAllChecked = pgItems.length > 0 && pgItems.every((g) => isChecked(g));
						const isPropertySomeChecked = pgItems.some((g) => isChecked(g));
						return (
							<div key={pg.sgUUID}>
								<span ref={(el) => { propertyRefsMap.current[pg.sgUUID] = el; }} style={{ display: 'block', height: 0 }} />
								<div
									className={style.assignmentModalPropertyHeader}
								>
									<input
										type="checkbox"
										checked={isPropertyAllChecked}
										ref={(node) => { if (node) node.indeterminate = isPropertySomeChecked && !isPropertyAllChecked; }}
										onChange={(e) => { if (e.target.checked) assignProperty(pg.uuids); else clearProperty(pg.uuids); }}
										onClick={(e) => e.stopPropagation()}
									/>
									<span className={style.assignmentModalPropertyName}>{pg.sgName}</span>
									<span className={style.assignmentModalPropertyCount}>{checkedCount}/{pgItems.length}</span>
									<span
										className={`${style.assignmentModalToggleLink} ${style.assignmentModalPropertyToggle}`}
										onClick={(e) => { e.stopPropagation(); isPropertyAllChecked ? clearProperty(pg.uuids) : assignProperty(pg.uuids); }}
									>
										{isPropertyAllChecked ? 'Deselect all' : 'Select all'}
									</span>
								</div>
								{pgItems.map((g) => (
									<div
										key={g.uuid}
										className={`${style.assignmentModalItem} ${style.assignmentModalItemIndented}${g.grouped ? ` ${style.assignmentModalGroupItem}` : ''}`}
										onClick={() => toggle(g)}
									>
										<input type="checkbox" checked={isChecked(g)} onChange={() => toggle(g)} onClick={(e) => e.stopPropagation()} />
										<span className={g.grouped ? style.assignmentModalGroupLabel : ''}>{g.name || g.uuid}</span>
										{g.grouped && (
											<span
												className={style.assignmentModalToggleLink}
												onClick={(e) => { e.stopPropagation(); isChecked(g) ? clearGroup(g) : assignGroup(g); }}
											>
												{isChecked(g) ? 'Deselect all' : 'Select all'}
											</span>
										)}
									</div>
								))}
							</div>
						);
					}) : deduped.map((g) => (
						<div
							key={g.uuid}
							className={`${style.assignmentModalItem}${g.grouped ? ` ${style.assignmentModalGroupItem}` : ''}`}
							onClick={() => toggle(g)}
						>
							<input type="checkbox" checked={isChecked(g)} onChange={() => toggle(g)} onClick={(e) => e.stopPropagation()} />
							<span className={g.grouped ? style.assignmentModalGroupLabel : ''}>{g.name || g.uuid}</span>
							{g.grouped && (
								<span
									className={style.assignmentModalToggleLink}
									onClick={(e) => { e.stopPropagation(); isChecked(g) ? clearGroup(g) : assignGroup(g); }}
								>
									{isChecked(g) ? 'Deselect all' : 'Select all'}
								</span>
							)}
						</div>
					))}
				</div>
				<div className={style.assignmentModalFooter}>
					<div className="button" onClick={save}>Save</div>
					<div className="button button-outlined" onClick={onClose}>Cancel</div>
				</div>
			</div>
		</div>
	);
};

const readCachedViewPrefs = (cacheKey) => {
	try {
		const json = localStorage.getItem(cacheKey);
		if (!json) return null;
		const prefs = JSON.parse(json);
		if (prefs && prefs.__v === LISTING_VIEW_CACHE_VERSION && typeof prefs === 'object' && !Array.isArray(prefs)) {
			return prefs;
		}
	} catch (e) { /* ignore */ }
	return null;
};

const readCachedHiddenLeftColIds = (cacheKey) => {
	try {
		const prefs = readCachedViewPrefs(cacheKey);
		if (prefs && Array.isArray(prefs.hiddenLeftColIds)) {
			return prefs.hiddenLeftColIds;
		}
	} catch (e) { /* ignore */ }
	return null;
};

const HeaderCheckbox = ({ array, selectedRowUUIDs, setSelectedRowUUIDs, toggleSelectSingleRow }) => {
	const allSelected = array.length > 0 && array.every((row) => selectedRowUUIDs.has(row.uuid));
	const someSelected = !allSelected && array.some((row) => selectedRowUUIDs.has(row.uuid));
	const ref = React.useRef();
	React.useEffect(() => {
		if (ref.current) ref.current.indeterminate = someSelected;
	}, [ someSelected, ]);
	return (
		<input
			type="checkbox"
			ref={ref}
			checked={allSelected}
			onChange={(e) => {
				const shouldSelectAll = e.target.checked;
				setSelectedRowUUIDs(new Set(shouldSelectAll ? array.map((r) => r.uuid) : []));
				if (typeof toggleSelectSingleRow === 'function') {
					array.forEach((row, index) => {
						const isCurrentlySelected = selectedRowUUIDs.has(row.uuid);
						if (isCurrentlySelected !== shouldSelectAll) {
							toggleSelectSingleRow(row.uuid, index);
						}
					});
				}
			}}
			onClick={(e) => e.stopPropagation()}
		/>
	);
};

const RowCheckboxCell = ({ row, selectedRowUUIDs, setSelectedRowUUIDs, toggleSelectSingleRow }) => {
	const uuid = row.original.uuid;
	return (
		<div className={style.rowSelectCheckbox}>
			<input
				type="checkbox"
				checked={selectedRowUUIDs.has(uuid)}
				onChange={(e) => {
					e.stopPropagation();
					setSelectedRowUUIDs((prev) => {
						const next = new Set(prev);
						if (e.target.checked) {
							next.add(uuid);
						} else {
							next.delete(uuid);
						}
						if (typeof toggleSelectSingleRow === 'function') {
							toggleSelectSingleRow(uuid, row.index);
						}
						return next;
					});
				}}
				onClick={(e) => e.stopPropagation()}
			/>
		</div>
	);
};

// Column types that should have a small default width (user can expand)
const SMALL_DEFAULT_FIELD_TYPES = new Set([ 'Location (apt/suite)', 'Building', 'Building Picker', ]);

const BulkListingAssignmentModal = ({ listingGroups, fd, onSave, onClose }) => {
	const [search, setSearch,] = useState('');
	const [pendingUUIDs, setPendingUUIDs,] = useState([]);

	const propertyGroups = useMemo(() => {
		if (!fd) return [];
		const groups = [];
		Object.entries(fd).forEach(([key, val,]) => {
			if (!key.startsWith('system_group.')) return;
			const sgUUID = key.split('.')[1];
			const sgName = (val && val.display_name) ? val.display_name : sgUUID;
			const systemIDsInProperty = new Set(
				Object.keys(val).filter((k) => k.startsWith('system.')).map((k) => k.split('.')[1])
			);
			const groupsInProperty = listingGroups.filter((g) =>
				g.systemM1Keys?.some((k) => systemIDsInProperty.has(k.split('.')[1]))
			);
			if (groupsInProperty.length > 0) {
				groups.push({ sgUUID, sgName, uuids: groupsInProperty.map((g) => g.uuid), });
			}
		});
		groups.sort((a, b) => a.sgName.localeCompare(b.sgName));
		return groups;
	}, [fd, listingGroups,]);

	const propertyGroupItems = useMemo(() => {
		if (!fd) return {};
		const result = {};
		propertyGroups.forEach((pg) => {
			const systemIDsInProperty = new Set();
			Object.entries(fd).forEach(([key, val,]) => {
				if (!key.startsWith(`system_group.${pg.sgUUID}`)) return;
				Object.keys(val).forEach((subKey) => {
					if (subKey.startsWith('system.')) systemIDsInProperty.add(subKey.split('.')[1]);
				});
			});
			result[pg.sgUUID] = listingGroups.filter((g) =>
				g.systemM1Keys?.some((k) => systemIDsInProperty.has(k.split('.')[1]))
			);
		});
		return result;
	}, [fd, propertyGroups, listingGroups,]);

	const deduped = listingGroups;

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return deduped;
		return deduped.filter((g) => (g.name || '').toLowerCase().includes(q));
	}, [deduped, search,]);

	const isChecked = useCallback((g) => pendingUUIDs.includes(g.uuid), [pendingUUIDs,]);

	const toggle = useCallback((g) => {
		setPendingUUIDs((prev) =>
			prev.includes(g.uuid) ? prev.filter((id) => id !== g.uuid) : [...prev, g.uuid,]
		);
	}, []);

	const assignGroup = useCallback((g) => {
		setPendingUUIDs((prev) => prev.includes(g.uuid) ? prev : [...prev, g.uuid,]);
	}, []);

	const clearGroup = useCallback((g) => {
		setPendingUUIDs((prev) => prev.filter((id) => id !== g.uuid));
	}, []);

	const assignProperty = useCallback((uuids) => {
		setPendingUUIDs((prev) => { const s = new Set(prev); uuids.forEach((u) => s.add(u)); return Array.from(s); });
	}, []);

	const clearProperty = useCallback((uuids) => {
		setPendingUUIDs((prev) => { const s = new Set(prev); uuids.forEach((u) => s.delete(u)); return Array.from(s); });
	}, []);

	const totalCount = deduped.length;
	const selectedCount = useMemo(() => deduped.filter((g) => pendingUUIDs.includes(g.uuid)).length, [deduped, pendingUUIDs,]);
	const isAllAssigned = useMemo(() => totalCount > 0 && deduped.every((g) => pendingUUIDs.includes(g.uuid)), [deduped, pendingUUIDs, totalCount,]);

	const assignAllCheckboxRef = useRef(null);
	useEffect(() => {
		if (assignAllCheckboxRef.current) {
			assignAllCheckboxRef.current.indeterminate = selectedCount > 0 && !isAllAssigned;
		}
	}, [selectedCount, isAllAssigned,]);

	const propertyRefsMap = useRef({});
	const assignmentModalListRef = useRef(null);

	const save = useCallback(() => {
		onSave(pendingUUIDs);
		onClose();
	}, [onSave, pendingUUIDs, onClose,]);

	const isSearching = search.trim().length > 0;

	return (
		<div className={style.assignmentModalOverlay} onClick={onClose}>
			<div className={style.assignmentModal} onClick={(e) => e.stopPropagation()}>
				<div className={style.assignmentModalHeader}>
					<div className={style.assignmentModalTitleGroup}>
						<span className={style.assignmentModalTitle}>Assign To Systems</span>
					</div>
					<span className={style.assignmentModalClose} onClick={onClose}>×</span>
				</div>
				<div className={style.assignmentModalSearch}>
					<div className={style.assignmentModalSearchWrapper}>
						<span className={style.assignmentModalSearchIcon}><CustomIcon icon="search" /></span>
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={`Search ${totalCount} systems...`}
							autoFocus
						/>
						{selectedCount > 0 && (
							<span className={style.assignmentModalSearchCount}>{selectedCount} selected</span>
						)}
					</div>
				</div>
				<div className={style.assignmentModalActions}>
					<label className={style.assignmentModalAssignAllLabel}>
						<input
							type="checkbox"
							checked={isAllAssigned}
							ref={assignAllCheckboxRef}
							onChange={(e) => { if (e.target.checked) { setPendingUUIDs(deduped.map((g) => g.uuid)); } else { setPendingUUIDs([]); } }}
							onClick={(e) => e.stopPropagation()}
						/>
						<span>Assign to all {totalCount} systems</span>
					</label>
					<div className="button" onClick={() => setPendingUUIDs([])}>Clear All</div>
				</div>
				{!isSearching && propertyGroups.length > 0 && (
					<div className={style.assignmentModalQuickJump}>
						<span className={style.assignmentModalQuickJumpLabel}>Quick:</span>
						{propertyGroups.map((pg) => (
							<span
								key={pg.sgUUID}
								className={style.assignmentModalQuickJumpItem}
								onClick={() => {
									const el = propertyRefsMap.current[pg.sgUUID];
									const listEl = assignmentModalListRef.current;
									if (el && listEl) {
										listEl.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
									}
								}}
							>
								{pg.sgName}
							</span>
						))}
					</div>
				)}
				<div className={style.assignmentModalList} ref={assignmentModalListRef}>
					{isSearching ? (
						<>
							{filtered.map((g) => (
								<div
									key={g.uuid}
									className={`${style.assignmentModalItem}${g.grouped ? ` ${style.assignmentModalGroupItem}` : ''}`}
									onClick={() => toggle(g)}
								>
									<input type="checkbox" checked={isChecked(g)} onChange={() => toggle(g)} onClick={(e) => e.stopPropagation()} />
									<span className={g.grouped ? style.assignmentModalGroupLabel : ''}>{g.name || g.uuid}</span>
									{g.grouped && (
										<span
											className={style.assignmentModalToggleLink}
											onClick={(e) => { e.stopPropagation(); isChecked(g) ? clearGroup(g) : assignGroup(g); }}
										>
											{isChecked(g) ? 'Deselect all' : 'Select all'}
										</span>
									)}
								</div>
							))}
							{filtered.length === 0 && <div className={style.assignmentModalEmpty}>No systems found.</div>}
						</>
					) : propertyGroups.length > 0 ? propertyGroups.map((pg) => {
						const pgItems = propertyGroupItems[pg.sgUUID] || [];
						if (pgItems.length === 0) return null;
						const checkedCount = pgItems.filter((g) => isChecked(g)).length;
						const isPropertyAllChecked = pgItems.length > 0 && pgItems.every((g) => isChecked(g));
						const isPropertySomeChecked = pgItems.some((g) => isChecked(g));
						return (
							<React.Fragment key={pg.sgUUID}>
								<span ref={(el) => { propertyRefsMap.current[pg.sgUUID] = el; }} style={{ display: 'block', height: 0 }} />
								<div
									className={style.assignmentModalPropertyHeader}
								>
									<input
										type="checkbox"
										checked={isPropertyAllChecked}
										ref={(node) => { if (node) node.indeterminate = isPropertySomeChecked && !isPropertyAllChecked; }}
										onChange={(e) => { if (e.target.checked) assignProperty(pg.uuids); else clearProperty(pg.uuids); }}
										onClick={(e) => e.stopPropagation()}
									/>
									<span className={style.assignmentModalPropertyName}>{pg.sgName}</span>
									<span className={style.assignmentModalPropertyCount}>{checkedCount}/{pgItems.length}</span>
									<span
										className={`${style.assignmentModalToggleLink} ${style.assignmentModalPropertyToggle}`}
										onClick={(e) => { e.stopPropagation(); isPropertyAllChecked ? clearProperty(pg.uuids) : assignProperty(pg.uuids); }}
									>
										{isPropertyAllChecked ? 'Deselect all' : 'Select all'}
									</span>
								</div>
								{pgItems.map((g) => (
									<div
										key={g.uuid}
										className={`${style.assignmentModalItem} ${style.assignmentModalItemIndented}${g.grouped ? ` ${style.assignmentModalGroupItem}` : ''}`}
										onClick={() => toggle(g)}
									>
										<input type="checkbox" checked={isChecked(g)} onChange={() => toggle(g)} onClick={(e) => e.stopPropagation()} />
										<span className={g.grouped ? style.assignmentModalGroupLabel : ''}>{g.name || g.uuid}</span>
										{g.grouped && (
											<span
												className={style.assignmentModalToggleLink}
												onClick={(e) => { e.stopPropagation(); isChecked(g) ? clearGroup(g) : assignGroup(g); }}
											>
												{isChecked(g) ? 'Deselect all' : 'Select all'}
											</span>
										)}
									</div>
								))}
							</React.Fragment>
						);
					}) : deduped.map((g) => (
						<div
							key={g.uuid}
							className={`${style.assignmentModalItem}${g.grouped ? ` ${style.assignmentModalGroupItem}` : ''}`}
							onClick={() => toggle(g)}
						>
							<input type="checkbox" checked={isChecked(g)} onChange={() => toggle(g)} onClick={(e) => e.stopPropagation()} />
							<span className={g.grouped ? style.assignmentModalGroupLabel : ''}>{g.name || g.uuid}</span>
							{g.grouped && (
								<span
									className={style.assignmentModalToggleLink}
									onClick={(e) => { e.stopPropagation(); isChecked(g) ? clearGroup(g) : assignGroup(g); }}
								>
									{isChecked(g) ? 'Deselect all' : 'Select all'}
								</span>
							)}
						</div>
					))}
				</div>
				<div className={style.assignmentModalFooter}>
					<div className="button" onClick={save}>Save</div>
					<div className="button button-outlined" onClick={onClose}>Cancel</div>
				</div>
			</div>
		</div>
	);
};

const ListingsTable = (props) => {
	const {
		app,
		accountUUID,
		systemGroupUUID,
		buildingsDataArray,
		listingFD,
		fd,
		array,
		columnsToHide,
		tableSettings,
		activeListingConfig,
		listingGroups,
		listingCollectionHasListingGroupRelation,
		getParentListingData,
		listingToSystemsMap,
		toggleSelectAllRows,
		toggleSelectSingleRow,
		listingConfigUUID,
		listingConfigToListingsMap,
		parentsPrimaryColumnData,
		setGroupValue,
		getChildRelations,
		findRootListings,
		getParentComponentName,
		uniqueSettingsListForAdd,
		pendingChanges,
		listingToListingCollectionsMap,
		updateListing,
		toggleGroup,
		bulkToggleGroupsAndSave,
		listingWidthCols,
		openView,
		deleteSelectedListings,
		listingsContentGroupColWidth,
		exportCSV,
		templateCSV,
		listingView,
		allRowsSelected,
		listingConfigHasScheduling,
		listingConfigIsIntegration,
		fetchListingDataByListingConfig,
		removeListingsFromFD,
		selectedListings,
		listingConfigsWithSystems,
	} = props;

	const { listingConfigMap, } = useListingConfig();

	const [ itemsPerPage, setItemsPerPage, ] = useState(50);
	const [ page, setPage, ] = useState(1);
	const [ pageNavigationBlocked, setPageNavigationBlocked, ] = useState(false);
	const [ filteredRowsCount, setFilteredRowsCount, ] = useState(array.length);

	const [ globalSearchQuery, setGlobalSearchQuery, ] = useState('');
	const [ selectedRowUUIDs, setSelectedRowUUIDs, ] = useState(new Set());
	const [ assignmentModalListing, setAssignmentModalListing, ] = useState(null);
	const [ parentAssignmentModalListing, setParentAssignmentModalListing, ] = useState(null);
	const [ isBulkProcessing, setIsBulkProcessing, ] = useState(false);
	const [ showBulkAssignWarning, setShowBulkAssignWarning, ] = useState(false);
	const [ showBulkAssignModal, setShowBulkAssignModal, ] = useState(false);
	const [ expandedAssignedToUUIDs, setExpandedAssignedToUUIDs, ] = useState(new Set());

	// Two-section layout state
	const wrapperRef = useRef(null);
	const [ tableBodyHeight, setTableBodyHeight, ] = useState(null);
	const [ systemsWidthPx, setSystemsWidthPx, ] = useState(null);
	const [ systemColWidthPx, setSystemColWidthPx, ] = useState(DEFAULT_SYSTEM_COL_WIDTH_PX);
	const [ showSystemsSection, setShowSystemsSection, ] = useState(true);
	const [ showColumnMenu, setShowColumnMenu, ] = useState(false);
	const [ columnMenuStyle, setColumnMenuStyle, ] = useState({ position: 'fixed', top: 0, left: 0, });
	const columnMenuRef = useRef(null);
	const [ viewPrefsHydrated, setViewPrefsHydrated, ] = useState(false);

	// Resize refs
	const resizingSystemsRef = useRef(false);
	const resizingSystemColsRef = useRef(false);
	const resizingLeftColRef = useRef(false);
	const resizingLeftColIdRef = useRef('');
	const startXRef = useRef(0);
	const startSystemsWidthRef = useRef(0);
	const startLeftColWidthPxRef = useRef(0);
	const dragRafRef = useRef(null);
	const dragThElRef = useRef(null);
	const dragCurrentWidthRef = useRef(null);
	const dragStyleElRef = useRef(null);
	const pendingClearDragColRef = useRef(null);
	const [ leftColOverrideWidths, setLeftColOverrideWidths, ] = useState({});

	const [ hiddenLeftColIds, setHiddenLeftColIds, ] = useState(() => {
		return readCachedHiddenLeftColIds(getListingsViewCacheKey(accountUUID, listingConfigUUID)) ?? [];
	});

	// System View column menu
	const [ hiddenSystemColIds, setHiddenSystemColIds, ] = useState([]);

	const [ canUserImport, setCanUserImport, ] = useState(false);

	const isRootTier = !!activeListingConfig?.root;
	const isAssignAllActive = !!listingFD[`listing_config.${listingConfigUUID}`]?.use_all_listings_inherited;

	useEffect(() => {
		if (hasPermission('csv_upload')) {
			setCanUserImport(true);
		}
	}, []);

	useEffect(() => {
		if (filteredRowsCount >= itemsPerPage && pageNavigationBlocked === true) {
			setPageNavigationBlocked(false);
		} else if (filteredRowsCount < itemsPerPage && pageNavigationBlocked === false) {
			setPageNavigationBlocked(true);
		}
	}, [ filteredRowsCount, pageNavigationBlocked, itemsPerPage, ]);

	// View prefs: hydrate from localStorage
	const viewCacheKey = useMemo(() => {
		return getListingsViewCacheKey(accountUUID, listingConfigUUID);
	}, [ accountUUID, listingConfigUUID, ]);

	useEffect(() => {
		setViewPrefsHydrated(false);
	}, [ viewCacheKey, ]);

	useEffect(() => {
		const cachedPrefs = readCachedViewPrefs(viewCacheKey);

		const safe = {
			...defaultListingsViewPrefs,
			...(cachedPrefs || {}),
		};

		setShowSystemsSection(typeof safe.showSystemsSection === 'boolean' ? safe.showSystemsSection : true);
		if (Number.isFinite(safe.systemsWidthPx)) {
			setSystemsWidthPx(safe.systemsWidthPx);
		}
		if (Number.isFinite(safe.systemColWidthPx)) {
			setSystemColWidthPx(safe.systemColWidthPx);
		}
		if (Array.isArray(safe.hiddenSystemColIds)) {
			setHiddenSystemColIds(safe.hiddenSystemColIds);
		}
		if (safe.leftColOverrideWidths && typeof safe.leftColOverrideWidths === 'object' && !Array.isArray(safe.leftColOverrideWidths)) {
			setLeftColOverrideWidths(safe.leftColOverrideWidths);
		}

		setViewPrefsHydrated(true);
	}, [ viewCacheKey, ]);

	const tableSettingsKeysSignature = useMemo(() => {
		const colKeys = Object.keys(tableSettings).filter((k) => !!tableSettings[k]);
		if (colKeys.length === 0) return '';
		const configEntry = listingConfigMap[listingConfigUUID] || {};
		const allBelongToCurrentConfig = colKeys.every((k) => (`listing_column.${k}` in configEntry));
		if (!allBelongToCurrentConfig) return '';
		return `${listingConfigUUID}:${colKeys.join(',')}`;
	}, [ listingConfigMap, listingConfigUUID, tableSettings, ]);

	useEffect(() => {
		if (!tableSettingsKeysSignature) return;
		const cached = readCachedHiddenLeftColIds(viewCacheKey);
		if (Array.isArray(cached)) {
			setHiddenLeftColIds(cached);
		} else {
			setHiddenLeftColIds(computeDefaultHiddenCols(tableSettings));
		}
	}, [ tableSettingsKeysSignature, viewCacheKey, ]);

	useEffect(() => {
		if (!viewPrefsHydrated) {
			return;
		}
		if (!tableSettingsKeysSignature) {
			return;
		}
		try {
			localStorage.setItem(viewCacheKey, JSON.stringify({
				showSystemsSection,
				systemsWidthPx,
				systemColWidthPx,
				hiddenSystemColIds,
				leftColOverrideWidths,
				hiddenLeftColIds,
				__v: LISTING_VIEW_CACHE_VERSION,
			}));
		} catch (err) {
			// storage unavailable
		}
	}, [ showSystemsSection, systemsWidthPx, systemColWidthPx, hiddenSystemColIds, leftColOverrideWidths, hiddenLeftColIds, viewCacheKey, viewPrefsHydrated, tableSettingsKeysSignature, ]);

	// Init container width for systems pane + calculate available height for sticky headers
	useEffect(() => {
		const init = () => {
			const containerWidth = wrapperRef.current ? wrapperRef.current.clientWidth : Math.max(window.innerWidth, 1200);
			const target = Math.round(containerWidth * 0.4);
			setSystemsWidthPx((prev) => {
				return prev !== null ? prev : target;
			});
			setSystemColWidthPx((prev) => {
				return prev !== DEFAULT_SYSTEM_COL_WIDTH_PX ? prev : Math.max(100, Math.floor(target / 4) - 4);
			});
			if (wrapperRef.current) {
				const wrapperRect = wrapperRef.current.getBoundingClientRect();
				const internalChromeHeight = 180;
				const available = window.innerHeight - Math.max(0, wrapperRect.top) - internalChromeHeight;
				setTableBodyHeight(Math.max(300, Math.floor(available)));
			}
		};
		init();
		window.addEventListener('resize', init);
		return () => window.removeEventListener('resize', init);
	}, []);

	// Systems-section resize handle
	const onSystemsHandleMouseMove = useCallback((e) => {
		if (!resizingSystemsRef.current) return;
		if (dragRafRef.current !== null) return;
		const clientX = e.clientX;
		dragRafRef.current = requestAnimationFrame(() => {
			dragRafRef.current = null;
			const dx = startXRef.current - clientX;
			const newWidth = Math.max(200, startSystemsWidthRef.current + dx);
			const containerWidth = wrapperRef.current ? wrapperRef.current.clientWidth : window.innerWidth;
			const minLeft = Math.max(400, Math.round(containerWidth * 0.6));
			const maxSystems = Math.min(containerWidth - minLeft, containerWidth - 300);
			setSystemsWidthPx(Math.min(maxSystems, newWidth));
		});
	}, []);

	const onSystemsHandleMouseUp = useCallback(() => {
		resizingSystemsRef.current = false;
		if (dragRafRef.current !== null) {
			cancelAnimationFrame(dragRafRef.current);
			dragRafRef.current = null;
		}
		document.removeEventListener('mousemove', onSystemsHandleMouseMove);
		document.removeEventListener('mouseup', onSystemsHandleMouseUp);
	}, [ onSystemsHandleMouseMove, ]);

	const onSystemsHandleMouseDown = useCallback((e) => {
		e.preventDefault();
		resizingSystemsRef.current = true;
		startXRef.current = e.clientX;
		startSystemsWidthRef.current = systemsWidthPx || 0;
		document.addEventListener('mousemove', onSystemsHandleMouseMove);
		document.addEventListener('mouseup', onSystemsHandleMouseUp);
	}, [ onSystemsHandleMouseMove, onSystemsHandleMouseUp, systemsWidthPx, ]);

	// System-column uniform resize handle
	const onSystemColHandleMouseMove = useCallback((e) => {
		if (!resizingSystemColsRef.current) return;
		if (dragRafRef.current !== null) return;
		const clientX = e.clientX;
		dragRafRef.current = requestAnimationFrame(() => {
			dragRafRef.current = null;
			const dx = clientX - startXRef.current;
			setSystemColWidthPx(Math.max(80, startSystemsWidthRef.current + dx));
		});
	}, []);

	const onSystemColHandleMouseUp = useCallback(() => {
		resizingSystemColsRef.current = false;
		if (dragRafRef.current !== null) {
			cancelAnimationFrame(dragRafRef.current);
			dragRafRef.current = null;
		}
		document.removeEventListener('mousemove', onSystemColHandleMouseMove);
		document.removeEventListener('mouseup', onSystemColHandleMouseUp);
	}, [ onSystemColHandleMouseMove, ]);

	const onSystemColHandleMouseDown = useCallback((e) => {
		e.preventDefault();
		resizingSystemColsRef.current = true;
		startXRef.current = e.clientX;
		startSystemsWidthRef.current = systemColWidthPx;
		document.addEventListener('mousemove', onSystemColHandleMouseMove);
		document.addEventListener('mouseup', onSystemColHandleMouseUp);
	}, [ onSystemColHandleMouseMove, onSystemColHandleMouseUp, systemColWidthPx, ]);

	// Inject CSS overrides during drag to avoid React re-renders on every mousemove.
	// This keeps resize smooth (0 re-renders during drag; 1 commit on mouseup).
	const applyDragStyle = useCallback((colId, width) => {
		if (!dragStyleElRef.current || !colId) return;
		const w = `${Math.round(width)}px`;
		const sel = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(colId) : colId;
		dragStyleElRef.current.textContent = `[id="${sel}"],[data-test-id="cell-${sel}"]{flex:0 0 ${w}!important;min-width:${w}!important;max-width:${w}!important}`;
	}, []);

	// Left-pane individual column resize
	const onLeftColHandleMouseMove = useCallback((e) => {
		if (!resizingLeftColRef.current) return;
		if (dragRafRef.current !== null) return;
		const clientX = e.clientX;
		dragRafRef.current = requestAnimationFrame(() => {
			dragRafRef.current = null;
			// The resize handle is on the LEFT side of the column.
			// Dragging left (dx < 0) widens the column; dragging right (dx > 0) narrows it.
			const dx = clientX - startXRef.current;
			let newWidth = Math.max(40, startLeftColWidthPxRef.current - dx);

			const thEl = dragThElRef.current;
			if (thEl && thEl.parentElement) {
				const rowEl = thEl.parentElement;
				const rowWidth = rowEl.getBoundingClientRect().width;
				const siblingCount = rowEl.children.length - 1; // exclude the dragged th
				const minSiblingsWidth = siblingCount * 60;
				const maxAllowed = Math.max(40, rowWidth - minSiblingsWidth);
				newWidth = Math.min(newWidth, maxAllowed);
			}

			// Enforce minimum width for assignedTo (20% of container)
			const colId = resizingLeftColIdRef.current;
			if (colId === 'assignedTo') {
				const containerWidth = wrapperRef.current ? wrapperRef.current.clientWidth : 1200;
				newWidth = Math.max(Math.round(containerWidth * 0.2), newWidth);
			}

			dragCurrentWidthRef.current = newWidth;

			if (colId) {
				applyDragStyle(colId, newWidth);
			}
		});
	}, [ applyDragStyle, ]);

	const onLeftColHandleMouseUp = useCallback(() => {
		resizingLeftColRef.current = false;
		if (dragRafRef.current !== null) {
			cancelAnimationFrame(dragRafRef.current);
			dragRafRef.current = null;
		}
		const colId = resizingLeftColIdRef.current;
		const finalWidth = dragCurrentWidthRef.current;
		if (colId && finalWidth !== null) {
			// Signal the useLayoutEffect to clear the drag-style after React commits the new width.
			pendingClearDragColRef.current = colId;
			setLeftColOverrideWidths((prev) => ({ ...prev, [colId]: finalWidth }));
			dragCurrentWidthRef.current = null;
		}
		document.removeEventListener('mousemove', onLeftColHandleMouseMove);
		document.removeEventListener('mouseup', onLeftColHandleMouseUp);
	}, [ onLeftColHandleMouseMove, ]);

	const onLeftColHandleMouseDown = useCallback((e, columnId) => {
		e.preventDefault();
		const colId = columnId || '';
		const thEl = e.currentTarget.parentElement;
		dragThElRef.current = thEl;
		const currentWidth = thEl ? thEl.getBoundingClientRect().width : 100;
		resizingLeftColRef.current = true;
		resizingLeftColIdRef.current = colId;
		startXRef.current = e.clientX;
		startLeftColWidthPxRef.current = currentWidth;
		// Pin the column at its current rendered width via CSS injection (no React re-render).
		applyDragStyle(colId, currentWidth);
		document.addEventListener('mousemove', onLeftColHandleMouseMove);
		document.addEventListener('mouseup', onLeftColHandleMouseUp);
	}, [ onLeftColHandleMouseMove, onLeftColHandleMouseUp, applyDragStyle, ]);

	const isLeftDataColumnResizable = useCallback((column) => {
		const id = column?.id || '';
		return !!id && id !== 'checkbox' && id !== 'scheduling' && !id.startsWith('groups.') && !id.startsWith('parents.');
	}, []);

	useEffect(() => {
		return () => {
			document.removeEventListener('mousemove', onSystemsHandleMouseMove);
			document.removeEventListener('mouseup', onSystemsHandleMouseUp);
			document.removeEventListener('mousemove', onSystemColHandleMouseMove);
			document.removeEventListener('mouseup', onSystemColHandleMouseUp);
			document.removeEventListener('mousemove', onLeftColHandleMouseMove);
			document.removeEventListener('mouseup', onLeftColHandleMouseUp);
		};
	}, [ onSystemsHandleMouseMove, onSystemsHandleMouseUp, onSystemColHandleMouseMove, onSystemColHandleMouseUp, onLeftColHandleMouseMove, onLeftColHandleMouseUp, ]);

	// Create the <style> element used for flicker-free column drag-resizing.
	useEffect(() => {
		const el = document.createElement('style');
		el.setAttribute('data-listings-drag', '1');
		document.head.appendChild(el);
		dragStyleElRef.current = el;
		return () => {
			if (el.parentNode) el.parentNode.removeChild(el);
		};
	}, []);

	// After React commits a new override width, clear the !important drag-style so the
	// React-managed flex style takes over seamlessly (no re-render needed for this part).
	useLayoutEffect(() => {
		if (pendingClearDragColRef.current !== null) {
			if (dragStyleElRef.current) dragStyleElRef.current.textContent = '';
			pendingClearDragColRef.current = null;
		}
	});

	// Show/Hide column menu
	const onShowHideButtonClick = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		if (showColumnMenu) {
			setShowColumnMenu(false);
			return;
		}
		const rect = e.currentTarget.getBoundingClientRect();
		setColumnMenuStyle({
			position: 'fixed',
			top:      `${Math.round(rect.bottom + 6)}px`,
			right:    `${Math.round(window.innerWidth - rect.right)}px`,
		});
		setShowColumnMenu(true);
	}, [ showColumnMenu, ]);

	useEffect(() => {
		if (!showColumnMenu) return;
		const onDocClick = (e) => {
			if (columnMenuRef.current && !columnMenuRef.current.contains(e.target)) {
				setShowColumnMenu(false);
			}
		};
		document.addEventListener('mousedown', onDocClick);
		return () => document.removeEventListener('mousedown', onDocClick);
	}, [ showColumnMenu, ]);

	const data = useMemo(() => {
		return array;
	}, [ array, ]);

	const visibleData = useMemo(() => {
		const term = globalSearchQuery.trim().toLowerCase();
		if (!term) return data;

		// Build building UUID → display name map for Building Picker column lookups.
		const buildingUUIDToNameMap = {};
		if (Array.isArray(buildingsDataArray)) {
			buildingsDataArray.forEach((b) => {
				const uuid = (b.m1Key || '').replace('building.', '');
				if (uuid) buildingUUIDToNameMap[uuid] = (b.vals?.displayName || '').toLowerCase();
			});
		}

		return data.filter((row) => {
			// 1. Search raw string values and JSON arrays stored in row.vals.
			const inVals = Object.values(row.vals || {}).some((v) => {
				if (typeof v !== 'string' || !v) return false;
				if (v.toLowerCase().includes(term)) return true;
				// JSON arrays (e.g. Location (apt/suite) stored as a JSON array string)
				try {
					const parsed = JSON.parse(v);
					if (Array.isArray(parsed)) return parsed.some((item) => String(item).toLowerCase().includes(term));
				} catch (_) {}
				return false;
			});
			if (inVals) return true;

			// 2. Search Building Picker columns by display name.
			//    The stored value is the building UUID; display name lives in buildingsDataArray.
			const inBuilding = Object.entries(row.cols || {}).some(([ colM1Key, col, ]) => {
				if (col.fieldType !== 'Building Picker') return false;
				const colUUID = colM1Key.replace('listing_column.', '');
				const buildingUUID = row.vals[colUUID];
				if (!buildingUUID || typeof buildingUUID !== 'string') return false;
				return (buildingUUIDToNameMap[buildingUUID] || '').includes(term);
			});
			if (inBuilding) return true;

			// 3. Assigned To: search group/system names AND property (system_group) names (root tier),
			//    or the parent listing's primary column value (child tiers).
			if (isRootTier) {
				// Collect the groups this row is assigned to.
				const assignedGroups = listingGroups.filter((g) => listingCollectionHasListingGroupRelation(g, row));
				if (assignedGroups.length > 0) {
					// Search group/system name.
					const inGroups = assignedGroups.some((g) => (g.name || '').toLowerCase().includes(term));
					if (inGroups) return true;
					// Search the property (system_group) names that contain any of the assigned systems.
					const inProperty = Object.entries(fd || {}).some(([ sgKey, sgVal, ]) => {
						if (!sgKey.startsWith('system_group.')) return false;
						const sgName = (sgVal?.display_name || '').toLowerCase();
						if (!sgName.includes(term)) return false;
						// Check if any assigned group has a system that belongs to this system_group.
						const systemsInSG = new Set(
							Object.keys(sgVal || {}).filter((k) => k.startsWith('system.')).map((k) => k.split('.')[1])
						);
						return assignedGroups.some((g) =>
							(g.systemM1Keys || []).some((k) => systemsInSG.has(k.split('.')[1]))
						);
					});
					if (inProperty) return true;
				}
			} else {
				const { parentsFirstColumnEntityKey, parentsFirstColumnAttr } = parentsPrimaryColumnData || {};
				if (parentsFirstColumnEntityKey && parentsFirstColumnAttr) {
					const inParents = (row.parent_listings || []).some((parentM1Key) => {
						const val = listingFD[parentM1Key]?.[parentsFirstColumnEntityKey]?.[parentsFirstColumnAttr] || '';
						return val.toLowerCase().includes(term);
					});
					if (inParents) return true;
				}
			}

			return false;
		});
	}, [ data, globalSearchQuery, isRootTier, listingGroups, listingCollectionHasListingGroupRelation, listingFD, fd, parentsPrimaryColumnData, buildingsDataArray, ]);

	useEffect(() => {
		setFilteredRowsCount(visibleData.length);
	}, [ visibleData, ]);

	useEffect(() => {
		setPage(1);
	}, [ globalSearchQuery, ]);

	useEffect(() => {
		if (app && app.setBulkProcessing) {
			app.setBulkProcessing(isBulkProcessing);
		}
	}, [ app, isBulkProcessing, ]);

	const checkboxColumn = useMemo(() => ({
		id:             'checkbox',
		Header:         () => (
			<HeaderCheckbox
				array={visibleData}
				selectedRowUUIDs={selectedRowUUIDs}
				setSelectedRowUUIDs={setSelectedRowUUIDs}
				toggleSelectSingleRow={toggleSelectSingleRow}
			/>
		),
		accessor:       (d) => d.uuid,
		Cell:           ({ row }) => (
			<RowCheckboxCell
				row={row}
				selectedRowUUIDs={selectedRowUUIDs}
				setSelectedRowUUIDs={setSelectedRowUUIDs}
				toggleSelectSingleRow={toggleSelectSingleRow}
			/>
		),
		disableFilters: true,
		disableSortBy:  true,
	}), [ visibleData, selectedRowUUIDs, setSelectedRowUUIDs, toggleSelectSingleRow, ]);

	const listingStaticColumns = (() => {
		const retval = [ checkboxColumn, ];

		if (listingConfigHasScheduling) {
			retval.push({
				id:       'scheduling',
				Header:   'Scheduling',
				accessor: (d) => {
					return d.schedule ? activeCheckbox('scheduling', d.vals[Object.keys(tableSettings)[0]] || '') : disabledSquare('scheduling', d.vals[Object.keys(tableSettings)[0]] || '');
				},
				disableFilters: true,
			});
		}

		let columnKeys = Object.keys(tableSettings);
		columnKeys
			.filter((columnKey) => {
				return !!tableSettings[columnKey];
			})
			.forEach((columnKey, i) => {
				let header = tableSettings[columnKey];

				if (tableSettings[columnKey].includes('\n')) {
					header = tableSettings[columnKey].split('\n')[0];
				}

				if (listingConfigIsIntegration && header) {
					header = header.charAt(0).toUpperCase() + header.slice(1);
				}

				if (i === 0) {
					retval.push(
						{
							id:       `${columnKey}`,
							Header:   header,
							accessor: (d) => {
								return d.vals[columnKey];
							},
							Cell: function Cell(row) {
								return (
									<div className={style.name} key={`${i}`}>
										{row.row.original.vals[columnKey]}
										{row.row.original.tags}
									</div>
								);
							},
							filter: filterByProp,
						},
						{
							id:       'columnToHide',
							Header:   'Hidden column for filtering',
							accessor: columnKey,
						}
					);
				}
				if (i > 0) {
					retval.push(
						{
							id:       `${columnKey}`,
							Header:   header,
							accessor: (d) => {
								const fieldType = d?.cols?.[`listing_column.${columnKey}`]?.fieldType;
								if (fieldType === 'Location (apt/suite)' || fieldType === 'Concatenator') {
									return parseLandmarkID(d.vals[columnKey]);
								}
								return d.vals[columnKey];
							},
						}
					);
				}
			});

		if (activeListingConfig?.root && !isAssignAllActive) {
			listingGroups.forEach((listingCollection, i) => {
				let listingCollectionName = listingCollection.name || listingCollection.uuid;

				if (listingCollection.grouped) {
					let elms = [];
					for (let systemM1Key of listingCollection.systemM1Keys) {
						if (listingFD[systemM1Key]) {
							elms.push(<div key={systemM1Key}>{listingFD[systemM1Key].display_name}</div>);
						}
					}

					let rolloverConfigData =
					<>
						<div className={style.rolloverHeader}>
							<CustomIcon icon="info-circle"/> {listingCollection.name}
						</div>
						<div className={style.rolloverContent}>
							<p>This Content Group will display selected<br/> items on the following systems:</p>
							<br/>
							{elms}
						</div>
					</>;

					listingCollectionName = (
						<div id={`${listingCollection.uuid}_${i}`} className={style.configInfo}>
							<div className={style.configInfoHeader}>
								<div className={style.nameContainer}>
									{listingCollection.name}
								</div>
								<div className={style.iconContainer}>
									<CustomIcon icon="info-circle"/>
								</div>
							</div>
							<div className={style.rolloverBox}>{rolloverConfigData}</div>
						</div>
					);
				}

				const SelectAllCheckboxFilter = ({
					column: {
						id,
						preFilteredRows,
					},
				}) => {
					const checkboxRef = React.useRef();
					const rowCount = preFilteredRows.length;
					let checkedRowCount = preFilteredRows.reduce((count, row) => {
						return count + (listingCollectionHasListingGroupRelation(listingCollection, row.original) ? 1 : 0);
					}, 0);

					const [ checked, setChecked, ] = useState(checkedRowCount === rowCount);

					const onChange = (e) => {
						let targetChecked = e.target.checked;
						let toggleRows = [];
						let groupID = id.split('.')[1];

						preFilteredRows.forEach((row) => {
							let hasGreenCheck = listingCollectionHasListingGroupRelation(listingCollection, row.original);
							if ((targetChecked && !hasGreenCheck) || (!targetChecked && hasGreenCheck)) {
								toggleRows.push(row.index);
							}
						});

						if (toggleRows.length > 0) {
							let group = listingGroups.find((listingGroup) => {
								return listingGroup.uuid === groupID;
							});
							toggleGroup(group, ...toggleRows);
						}

						setChecked(targetChecked);
					};

					React.useEffect(() => {
					  if (checkboxRef.current) {
							checkboxRef.current.indeterminate = checkedRowCount > 0 && checkedRowCount < rowCount;
					  }
					}, [ checkedRowCount, rowCount, ]);

					return (
					  <input
							type="checkbox"
							ref={checkboxRef}
							checked={checked}
							onChange={onChange}
							onClick={(e) => {
								return e.stopPropagation();
							}}
							title={`Select ${(checked) ? 'None' : 'All'}`}
					  />
					);
				};

				retval.push(
					{
						id:       `groups.${listingCollection.uuid}`,
						Header:   listingCollectionName,
						Filter:   SelectAllCheckboxFilter,
						accessor: (d) => {
							const systemName = listingCollection.name || '';
							const listingName = d.vals[Object.keys(tableSettings)[0]] || '';
							return listingCollectionHasListingGroupRelation(listingCollection, d) ?
								activeCheckbox(systemName, listingName) :
								disabledSquare(systemName, listingName);
						},
						sortType: (a, b) => {
							return listingCollectionHasListingGroupRelation(listingCollection, b.original) - listingCollectionHasListingGroupRelation(listingCollection, a.original);
						},
						// disableFilters: true,
					}
				);
			});
		}

		return retval;
	})();

	// Split into data columns (left pane) vs system/group columns (right pane, Tier 1 only)
	const { dataColumns: listingDataColumns, systemColumns: listingSystemColumns } = useMemo(() => {
		const dataColumns = listingStaticColumns.filter((col) => {
			return !col.id?.startsWith('groups.');
		});

		if (isRootTier) {
			dataColumns.push({
				id:             'assignedTo',
				Header:         'Assigned To',
				disableFilters: true,
				accessor:       (d) => listingGroups.filter((g) => listingCollectionHasListingGroupRelation(g, d)).length,
				Cell:           function AssignedToCell({ row }) {
					const listing = row.original;
					const isExpanded = expandedAssignedToUUIDs.has(listing.uuid);
					const onToggleExpand = () => setExpandedAssignedToUUIDs((prev) => {
						const next = new Set(prev);
						if (next.has(listing.uuid)) next.delete(listing.uuid); else next.add(listing.uuid);
						return next;
					});
					return (
						<AssignedToChips
							listing={listing}
							listingGroups={listingGroups}
							listingFD={listingFD}
							fd={fd}
							listingCollectionHasListingGroupRelation={listingCollectionHasListingGroupRelation}
							toggleGroup={toggleGroup}
							rowIndex={row.index}
							isExpanded={isExpanded}
							onToggleExpand={onToggleExpand}
							isAssignAllActive={isAssignAllActive}
							onOpenModal={isAssignAllActive ? undefined : (e) => {
								e.stopPropagation();
								setAssignmentModalListing({ ...listing, _rowIndex: row.index });
							}}
						/>
					);
				},
			});
		} else if (activeListingConfig && !activeListingConfig.root) {
			dataColumns.push({
				id:             'assignedTo',
				Header:         'Assigned To',
				disableFilters: true,
				accessor:       (d) => (d.parent_listings || []).length,
				Cell:           function AssignedToCell({ row }) {
					const listing = row.original;
					return (
						<ParentListingChips
							listing={listing}
							listingFD={listingFD}
							parentsPrimaryColumnData={parentsPrimaryColumnData}
							onOpenModal={(e) => {
								e.stopPropagation();
								setParentAssignmentModalListing(listing);
							}}
						/>
					);
				},
			});
		}

		const systemColumns = listingStaticColumns.filter((col) => {
			return col.id?.startsWith('groups.');
		});
		return { dataColumns, systemColumns };
	}, [ listingStaticColumns, isRootTier, activeListingConfig, listingGroups, listingCollectionHasListingGroupRelation, listingToListingCollectionsMap, listingConfigMap, listingFD, fd, toggleGroup, expandedAssignedToUUIDs, parentsPrimaryColumnData, isAssignAllActive, ]);

	const systemColumnsWithTooltips = useMemo(() => {
		return listingSystemColumns.map((col) => {
			const rawHeader = col.Header;
			const fullName = typeof rawHeader === 'string' ? rawHeader :
				(col.id?.startsWith('groups.') ? (col.id.split('.')[1] || '') : '');

			return {
				...col,
				Header: () => {
					return (
						<div className={style.systemHeaderTooltipWrapper}>
							<div className={style.systemHeaderHeaderText}>{rawHeader}</div>
							{fullName && (
								<div className={style.systemHeaderTooltipBubble}>{fullName}</div>
							)}
						</div>
					);
				},
			};
		});
	}, [ listingSystemColumns, ]);

	const dataColumnsWrapped = useMemo(() => {
		return [
			{
				Header:  'ListingsTable',
				columns: listingDataColumns,
			},
		];
	}, [ listingDataColumns, ]);

	const systemColumnsWrapped = useMemo(() => {
		return [
			{
				Header:  'ListingsSystemsHeader',
				columns: systemColumnsWithTooltips,
			},
		];
	}, [ systemColumnsWithTooltips, ]);

	const alwaysHiddenColIds = useMemo(() => {
		return listingDataColumns
			.filter((col) => {
				const label = (typeof col.Header === 'string' ? col.Header : col.id || '');
				return isIntegrationBookkeepingColumn(label);
			})
			.map((col) => col.id)
			.filter(Boolean);
	}, [ listingDataColumns, ]);

	const leftTableColumnsToHide = useMemo(() => {
		const userManageableIds = new Set(Object.keys(tableSettings).filter((k) => !!tableSettings[k]));
		const systemOnlyFromParent = (columnsToHide || []).filter((id) => !userManageableIds.has(id));
		return [ ...systemOnlyFromParent, ...hiddenSystemColIds, ...hiddenLeftColIds, ...alwaysHiddenColIds, ];
	}, [ columnsToHide, hiddenSystemColIds, hiddenLeftColIds, alwaysHiddenColIds, tableSettings, ]);

	const pushSelectedToAll = useCallback((e) => {
		if (e) e.preventDefault();
		if (isBulkProcessing) return;
		if (isRootTier && isAssignAllActive) return;

		const selectedRows = data.filter((row) => selectedRowUUIDs.has(row.uuid));
		if (selectedRows.length === 0) return;

		const allGroupUUIDs = listingGroups.map((g) => g.uuid);

		listingView.setState({
			confirmModal:        true,
			confirmModalContent: (
				<>
					<p>{`Push ${selectedRows.length} listing${selectedRows.length > 1 ? 's' : ''} to all systems?`}</p>
					<div className="confirm-modal-buttons">
						<button className="button" onClick={(evt) => {
							evt.preventDefault();
							listingView.setState({ confirmModal: false, confirmModalContent: '' });
							setIsBulkProcessing(true);
							// Write the relations directly, not via toggleGroup(): toggleGroup
							// opens a confirm modal for non-exclusive groups and double-writes.
							// Chips refresh from the refetch below.
							const bulkObj = {};
							selectedRows.forEach((row) => {
								allGroupUUIDs.forEach((uuid) => {
									const group = listingGroups.find((g) => g.uuid === uuid);
									if (group && !listingCollectionHasListingGroupRelation(group, row)) {
										(group.listingCollectionM1Keys || []).forEach((m1Key) => {
											bulkObj[`${m1Key}.listing.${row.uuid}`] = true;
										});
									}
								});
							});

							const finish = () => {
								setIsBulkProcessing(false);
								setSelectedRowUUIDs(new Set());
								fetchListingDataByListingConfig(false);
							};

							if (Object.keys(bulkObj).length > 0) {
								app.setValue('listings', Object.keys(bulkObj), Object.values(bulkObj), () => {
									let completed = false;
									const safeFinish = () => {
										if (completed) return;
										completed = true;
										finish();
									};
									app.saveData('listings', safeFinish);
									setTimeout(safeFinish, 15000);
								});
							} else {
								finish();
							}
						}}>Yes</button>
						<button className="button" onClick={(evt) => {
							evt.preventDefault();
							listingView.setState({ confirmModal: false, confirmModalContent: '' });
						}}>No</button>
					</div>
				</>
			),
		});
	}, [ app, data, isBulkProcessing, listingGroups, listingCollectionHasListingGroupRelation, listingView, selectedRowUUIDs, fetchListingDataByListingConfig, isRootTier, isAssignAllActive, ]);


	const saveBulkAssignments = useCallback((uuids) => {
		if (isBulkProcessing) return;
		const selectedRows = data.filter((row) => selectedRowUUIDs.has(row.uuid));
		if (selectedRows.length === 0) return;

		setIsBulkProcessing(true);
		const groupByUUID = new Map(listingGroups.map((g) => [ g.uuid, g, ]));
		
		const bulkObj = {};

		selectedRows.forEach((row) => {
			listingGroups.forEach((group) => {
				if (listingCollectionHasListingGroupRelation(group, row)) {
					(group.listingCollectionM1Keys || []).forEach((m1Key) => {
						bulkObj[`${m1Key}.listing.${row.uuid}`] = false;
					});
				}
			});
			uuids.forEach((uuid) => {
				const group = groupByUUID.get(uuid);
				if (group) {
					(group.listingCollectionM1Keys || []).forEach((m1Key) => {
						bulkObj[`${m1Key}.listing.${row.uuid}`] = true;
					});
				}
			});
		});

		const finish = () => {
			setIsBulkProcessing(false);
			setSelectedRowUUIDs(new Set());
			fetchListingDataByListingConfig(false);
		};

		if (Object.keys(bulkObj).length > 0) {
			app.setValue('listings', Object.keys(bulkObj), Object.values(bulkObj), () => {
				let completed = false;
				const safeFinish = () => {
					if (completed) return;
					completed = true;
					finish();
				};
				app.saveData('listings', safeFinish);
				setTimeout(safeFinish, 15000);
			});
		} else {
			finish();
		}
	}, [ app, data, isBulkProcessing, listingGroups, listingCollectionHasListingGroupRelation, selectedRowUUIDs, fetchListingDataByListingConfig, ]);


	// One save per selected listing, chained — same shape as SlideshowTable's
	// saveBulkAssignments. Batching every row into one payload puts N children under a
	// single parent M1 key, and the server writes only the first (see AI_KB
	// gotchas/listing-to-listing-rel-one-m2-per-request.md).
	const saveBulkParentAssignments = useCallback((parentM1Keys) => {
		if (isBulkProcessing) return;
		const selectedRows = data.filter((row) => selectedRowUUIDs.has(row.uuid));
		if (selectedRows.length === 0) return;

		setIsBulkProcessing(true);

		const finish = () => {
			setIsBulkProcessing(false);
			setSelectedRowUUIDs(new Set());
			fetchListingDataByListingConfig(false);
		};

		const runAtIndex = (index) => {
			if (index >= selectedRows.length) {
				finish();
				return;
			}

			const row = selectedRows[index];
			const next = () => runAtIndex(index + 1);

			if (!row || !row.uuid) {
				next();
				return;
			}

			const rowObj = buildParentAssignmentPayload(row, parentM1Keys);

			const keys = Object.keys(rowObj);
			if (keys.length === 0) {
				next();
				return;
			}

			app.setValue('listings', keys, Object.values(rowObj), () => {
				let completed = false;
				const safeNext = () => {
					if (completed) return;
					completed = true;
					next();
				};
				app.saveData('listings', safeNext);
				setTimeout(safeNext, 15000);
			});
		};

		runAtIndex(0);
	}, [ app, data, isBulkProcessing, selectedRowUUIDs, fetchListingDataByListingConfig, ]);

	const clearAllAssignments = useCallback((e) => {
		if (e) e.preventDefault();
		if (isBulkProcessing) return;
		if (isRootTier && isAssignAllActive) return;

		const selectedRows = data.filter((row) => selectedRowUUIDs.has(row.uuid));
		if (selectedRows.length === 0) return;

		listingView.setState({
			confirmModal:        true,
			confirmModalContent: (
				<>
					<p>{`Clear all system assignments for ${selectedRows.length} listing${selectedRows.length > 1 ? 's' : ''}?`}</p>
					<div className="confirm-modal-buttons">
						<button className="button" onClick={(evt) => {
							evt.preventDefault();
							listingView.setState({ confirmModal: false, confirmModalContent: '' });
							setIsBulkProcessing(true);
							// Write the relations directly (see pushSelectedToAll), not toggleGroup().
							const bulkObj = {};
							selectedRows.forEach((row) => {
								listingGroups.forEach((group) => {
									if (listingCollectionHasListingGroupRelation(group, row)) {
										(group.listingCollectionM1Keys || []).forEach((m1Key) => {
											bulkObj[`${m1Key}.listing.${row.uuid}`] = false;
										});
									}
								});
							});
							
							const finish = () => {
								setIsBulkProcessing(false);
								setSelectedRowUUIDs(new Set());
								fetchListingDataByListingConfig(false);
							};

							if (Object.keys(bulkObj).length > 0) {
								app.setValue('listings', Object.keys(bulkObj), Object.values(bulkObj), () => {
									let completed = false;
									const safeFinish = () => {
										if (completed) return;
										completed = true;
										finish();
									};
									app.saveData('listings', safeFinish);
									setTimeout(safeFinish, 15000);
								});
							} else {
								finish();
							}
						}}>Yes</button>
						<button className="button" onClick={(evt) => {
							evt.preventDefault();
							listingView.setState({ confirmModal: false, confirmModalContent: '' });
						}}>No</button>
					</div>
				</>
			),
		});
	}, [ app, data, isBulkProcessing, listingGroups, listingCollectionHasListingGroupRelation, listingView, selectedRowUUIDs, fetchListingDataByListingConfig, isRootTier, isAssignAllActive, ]);

	let validConfig = false;
	for (const m1Key of (Object.keys(listingConfigMap[listingConfigUUID] || {}))) {
		if (m1Key.startsWith('listing_column')) {
			validConfig = true;
		}
	}

	let disabledUnlessChecked = `button ${style.button} button-disabled`;
	array.map((obj) => {
		if (obj.isChecked) {
			disabledUnlessChecked = 'button';
		}
	});

	let totalWidth = 0;

	// write as for of
	for (let [ listingColumnUUID, ] of Object.entries(listingFD)) {
		totalWidth += listingFD[`listing_config.${listingConfigUUID}`]?.[`listing_column.${listingColumnUUID}`]?.width || 0;
	}

	let parentsWidth = 100 - totalWidth;

	const handleDelete = useCallback(() => {
		let count = 0;
		let listingsHaveChildren = false;
		let nonExclusiveSystemPresent = false;
		let numberOfChildren = 0;

		const nonExclusiveSystemUUIDs = listingGroups
			.filter((listingGroup) => {
				return listingGroup.nonExclusive;
			})
			.reduce((_nonExclusiveSystemUUIDs, listingGroup) => {
				return [ ..._nonExclusiveSystemUUIDs, ...listingGroup.systemM1Keys, ];
			}, []);

		array.map((obj) => {
			if (obj.isChecked) {
				count++;

				for (const systemUUID of obj.systems) {
					if (nonExclusiveSystemUUIDs.includes(systemUUID)) {
						nonExclusiveSystemPresent = true;
					}
				}

				const childUUIDs = [];

				for (let [ m1Key, m1Data, ] of Object.entries(listingFD)) {
					if (m1Key === `listing.${obj.uuid}`) {
						for (let m2Key of Object.keys(m1Data)) {
							const sections = m2Key.split('.').length;
							const entityKey = `${m1Key}.${m2Key}`;
							if (m2Key.startsWith('listing.') && sections === 2) {
								childUUIDs.push(entityKey);
							}
						}
					}
				}

				numberOfChildren = childUUIDs.length;
				if (childUUIDs.length > 0) {
					listingsHaveChildren = true;
				}
			}
		});
		if (count === 0) {
			return;
		}

		if (nonExclusiveSystemPresent) {
			listingView.setState({
				confirmModal:        true,
				confirmModalContent: (
					<>
						<p>Cannot delete listings that are assigned to systems outside this group.</p>
						<div className="confirm-modal-buttons">
							<button className="button" onClick={(e) => {
								e.preventDefault();
								listingView.setState({
									confirmModal:        false,
									confirmModalContent: '',
								});
							}}>Okay</button>
						</div>
					</>
				),
			});
			return;
		} else if (count > 1 && listingsHaveChildren) {
			listingView.setState({
				confirmModal:        true,
				confirmModalContent: (
					<>
						<p>Cannot delete listings with children.</p>
						<div className="confirm-modal-buttons">
							<button className="button" onClick={(e) => {
								e.preventDefault();
								listingView.setState({
									confirmModal:        false,
									confirmModalContent: '',
								});
							}}>Okay</button>
						</div>
					</>
				),
			});
			return;
		}

		if (listingsHaveChildren) {
			let message = `This listing currently has ${numberOfChildren} record(s) associated to it. Please delete the associated record(s) first.`;
			listingView.setState({
				confirmModal:        true,
				confirmModalContent: (
					<>
						<p>{message}</p>
						<div className="confirm-modal-buttons">
							<button className="button" onClick={(e) => {
								e.preventDefault();
								listingView.setState({
									confirmModal:        false,
									confirmModalContent: '',
								});
							}}>Okay</button>
						</div>
					</>
				),
			});
			return;
		}
		listingView.setState({
			confirmModal:        true,
			confirmModalContent: (
				<>
					<p>{`Delete ${count} listing${count > 1 ? 's' : ''}?`}</p>
					<div className="confirm-modal-buttons">
						<button className="button" onClick={(e) => {
							e.preventDefault();
							deleteSelectedListings();
							// Clear the row-selection Set too, otherwise the bulk-action
							// banner stays up (with a stale, growing count) after delete.
							setSelectedRowUUIDs(new Set());
							listingView.setState({
								confirmModal:        false,
								confirmModalContent: '',
							});
						}}>Yes</button>
						<button className="button" onClick={(e) => {
							e.preventDefault();
							listingView.setState({
								confirmModal:        false,
								confirmModalContent: '',
							});
						}}>No</button>
					</div>
				</>
			),
		});
	}, [ array, deleteSelectedListings, listingFD, listingGroups, listingView, setSelectedRowUUIDs, ]);

	const secondaryNavButtons = useMemo(() => {
		// Common Add New button (non-integration only)
		const addNewBtn = {
			onClick: (e) => {
				e.preventDefault();
				const newListingUUID = `_${uuidv4()}`;
				app.openModal({
					modalSize:  2,
					showModal:  true,
					modalProps: {
						title:          'Add Listings',
						customClass:    'add-listings-modal',
						pendingChanges: app.hasPendingChanges(),
						jsx:            (
							<ListingsModal
								isEdit={false}
								listingFD={listingFD}
								fd={fd}
								pendingChanges={pendingChanges}
								listing={{
									uuid:            newListingUUID,
									parent_listings: [],
								}}
								listingToListingCollectionsMap={listingToListingCollectionsMap}
								listingToSystemsMap={listingToSystemsMap}
								listingConfigMap={listingConfigMap}
								findRootListings={findRootListings}
								updateListing={updateListing}
								accountUUID={accountUUID}
								buildingsDataArray={buildingsDataArray}
								listingConfigHasScheduling={listingConfigHasScheduling}
								activeListingConfig={activeListingConfig}
								listingGroups={listingGroups}
								setGroupValue={setGroupValue}
								getChildRelations={getChildRelations}
								getParentComponentName={getParentComponentName}
								uniqueSettingsListForAdd={uniqueSettingsListForAdd}
								listingConfigUUID={listingConfigUUID}
								parentsPrimaryColumnData={parentsPrimaryColumnData}
								listingConfigToListingsMap={listingConfigToListingsMap}
								fetchListingDataByListingConfig={fetchListingDataByListingConfig}
								removeListingsFromFD={removeListingsFromFD}
							/>
						),
					},
				});
			},
			className:  'button',
			icon:       'plus',
			text:       'Add New',
			dataTestId: 'addNewBtn',
		};

		const exportBtn = {
			onClick: (e) => {
				e.preventDefault();
				dlListingsExportAll();
				exportCSV();
			},
			className:  'button',
			icon:       'file-download',
			text:       'Export' + (selectedListings.length > 0 ? ' Selected' : ' All'),
			dataTestId: 'exportBtn',
		};

		const importBtn = {
			onClick: (e) => {
				e.preventDefault();
				if (!canUserImport) return;
				dlListingsImport();
				app.openModal({
					modalSize:  2,
					showModal:  true,
					modalProps: {
						title:          'Import Listings',
						customClass:    'test',
						pendingChanges: app.hasPendingChanges(),
						jsx:            (
							<ListingsUploadModal
								accountUUID={accountUUID}
								listingConfigUUID={listingConfigUUID}
								tableSettings={tableSettings}
								listingConfigMap={listingConfigMap}
								systemGroupUUID={systemGroupUUID}
								fetchListingDataByListingConfig={fetchListingDataByListingConfig}
							/>
						),
					},
				});
			},
			className:  'button' + (canUserImport ? '' : ' button-disabled'),
			icon:       'cloud-upload-alt',
			text:       'Import',
			dataTestId: 'importBtn',
		};

		const templateBtn = {
			onClick: (e) => {
				e.preventDefault();
				dlListingsTemplate();
				templateCSV();
			},
			className: 'button',
			icon:      'file-download',
			text:      'Get Template',
		};

		const deleteBtn = {
			onClick: (e) => {
				e.preventDefault();
				handleDelete();
			},
			className: disabledUnlessChecked,
			icon:      'trash',
			text:      'Delete',
		};

		if (listingConfigIsIntegration) {
			return [ exportBtn, deleteBtn, ];
		}

		return [ addNewBtn, exportBtn, importBtn, templateBtn, deleteBtn, ];
	}, [ selectedListings.length, canUserImport, disabledUnlessChecked, listingConfigIsIntegration, app, listingFD, fd, pendingChanges, listingToListingCollectionsMap, listingToSystemsMap, listingConfigMap, findRootListings, updateListing, accountUUID, buildingsDataArray, listingConfigHasScheduling, activeListingConfig, listingGroups, setGroupValue, getChildRelations, getParentComponentName, uniqueSettingsListForAdd, listingConfigUUID, parentsPrimaryColumnData, listingConfigToListingsMap, fetchListingDataByListingConfig, removeListingsFromFD, exportCSV, tableSettings, systemGroupUUID, templateCSV, handleDelete, ]);

	function getPageItems(currentPage, totalPages) {
		const pages = [];
		const delta = 2; // Number of pages to show around current
		const leftRange = currentPage - delta;
		const rightRange = currentPage + delta;

		pages.push(1);

		if (currentPage - delta > 2) {
			pages.push('...');
		}

		for (let i = Math.max(2, leftRange); i <= Math.min(totalPages - 1, rightRange); i++) {
			pages.push(i);
		}

		if (currentPage + delta < totalPages - 1) {
			pages.push('...');
		}

		if (totalPages > 1) {
			pages.push(totalPages);
		}

		return pages;
	}

	const tertiaryNavButtons = useMemo(() => {
		const totalPages = Math.ceil(filteredRowsCount / itemsPerPage);
		const pageItems = getPageItems(page, totalPages);

		return [
			...pageItems.map((item, index) => {
				if (item === '...') {
					return {
						key:       `ellipsis-${index}`,
						className: 'paginationEllipsis',
						text:      '...',
					};
				}
				return {
					key:       `page-${item}`,
					className: `button ${page === item ? 'active' : ''}`,
					onClick:   () => {
						setPage(item);
					},
					text: item.toString(),
				};
			}),
		];
	}, [ filteredRowsCount, itemsPerPage, page, ]);

	const uuidDataColIds = useMemo(() => {
		return listingDataColumns.map((col) => col.id).filter((id) => id && listingWidthCols[id]);
	}, [ listingDataColumns, listingWidthCols, ]);

	const totalDataGrowFactors = useMemo(() => {
		return uuidDataColIds.reduce((sum, id) => sum + (listingWidthCols[id]?.width || 1), 0);
	}, [ uuidDataColIds, listingWidthCols, ]);

	const parentColGrowFactor = useMemo(() => {
		return Math.max(20, Math.round(totalDataGrowFactors / 4));
	}, [ totalDataGrowFactors, ]);

	const CENTER_ALIGNED_FIELD_TYPES = new Set([
		'Location (apt/suite)',
		'Building',
		'Building Picker',
		'Text Small',
		'Floor',
		'Number',
		'Image',
		'Photo',
		'PDF',
		'Directional Arrows',
		'Concatenator',
		'Checkbox',
		'Video',
		'Logo',
	]);

	const columnClassMap = useMemo(() => {
		const map = {};
		map['scheduling'] = 'centeredCol';
		// Use the cols fieldType from any available row — this is always correct for all tiers
		// (root + child) and avoids unreliable activeListingConfig/listingFD sub-key resolution.
		const sampleCols = array.length > 0 ? (array[0].cols || {}) : {};
		Object.keys(tableSettings).forEach((columnUUID) => {
			const colKey = `listing_column.${columnUUID}`;
			const col = sampleCols[colKey];
			if (col) {
				if (CENTER_ALIGNED_FIELD_TYPES.has(col.fieldType || '')) {
					map[columnUUID] = 'centeredCol';
				}
				return;
			}
			// Fallback: resolve via listingFD when no row data is available yet.
			const listingColumn = listingFD[colKey];
			if (!listingColumn) return;
			const listingColumnTypeM1Key = Object.keys(listingColumn).find((k) => k.startsWith('listing_column_type.'));
			if (!listingColumnTypeM1Key) return;
			const listingColumnType = listingFD[listingColumnTypeM1Key];
			if (!listingColumnType) return;
			if (CENTER_ALIGNED_FIELD_TYPES.has(listingColumnType.display_name)) {
				map[columnUUID] = 'centeredCol';
			}
		});
		return map;
	}, [ listingFD, tableSettings, array, ]);

	// Column UUIDs that are Building / Location (apt/suite) type — capped to a small default width
	const smallDefaultColIds = useMemo(() => {
		const ids = new Set();
		const sampleCols = array.length > 0 ? (array[0].cols || {}) : {};
		Object.keys(tableSettings).forEach((columnUUID) => {
			const colKey = `listing_column.${columnUUID}`;
			const col = sampleCols[colKey];
			if (col) {
				if (SMALL_DEFAULT_FIELD_TYPES.has(col.fieldType || '')) ids.add(columnUUID);
				return;
			}
			const listingColumn = listingFD[colKey];
			if (!listingColumn) return;
			const listingColumnTypeM1Key = Object.keys(listingColumn).find((k) => k.startsWith('listing_column_type.'));
			if (!listingColumnTypeM1Key) return;
			const listingColumnType = listingFD[listingColumnTypeM1Key];
			if (!listingColumnType) return;
			if (SMALL_DEFAULT_FIELD_TYPES.has(listingColumnType.display_name)) ids.add(columnUUID);
		});
		return ids;
	}, [ listingFD, tableSettings, array, ]);

	const applyFlexOverrides = useCallback((id, colStyles) => {
		if (leftColOverrideWidths[id] !== undefined) {
			const px = leftColOverrideWidths[id];
			const { width: _w, position: _p, left: _l, ...rest } = colStyles;
			return { ...rest, width: `${px}px`, flex: `0 0 ${px}px` };
		}
		if (uuidDataColIds.includes(id)) {
			const growFactor = listingWidthCols[id]?.width || 1;
			const { width: _w, position: _p, left: _l, ...rest } = colStyles;
			return { ...rest, flex: `${growFactor} 1 0px`, minWidth: '60px' };
		}
		if (id.startsWith('parents.')) {
			const { width: _w, ...rest } = colStyles;
			return { ...rest, flex: `${parentColGrowFactor} 1 0px`, minWidth: '120px' };
		}
		return colStyles;
	}, [ leftColOverrideWidths, uuidDataColIds, listingWidthCols, parentColGrowFactor, ]);

	const theadColStyles = useCallback((column) => {
		const id = column?.id || '';
		if (id === 'checkbox') {
			return { width: '50px', minWidth: '50px', maxWidth: '50px', flexShrink: 0 };
		}
		if (id === 'scheduling') {
			return { width: '80px', minWidth: '80px', maxWidth: '80px', flexShrink: 0 };
		}
		if (id === 'assignedTo') {
			const ALWAYS_HIDDEN_IDS = new Set([ 'checkbox', 'columnToHide', 'assignedTo', 'scheduling', ]);
			const togglableCols = (listingDataColumns || []).filter((col) => col && col.id && !ALWAYS_HIDDEN_IDS.has(col.id));
			const visibleTogglableCols = togglableCols.filter((col) => !(leftTableColumnsToHide || []).includes(col.id));
			if (visibleTogglableCols.length === 0) {
				const grow = Math.max(1, totalDataGrowFactors || 1);
				return { flex: `${grow} 1 0px`, minWidth: '20%' };
			}
			// Honour user override but never shrink below 20%
			if (leftColOverrideWidths[id] !== undefined) {
				const px = leftColOverrideWidths[id];
				return { flex: `0 0 ${px}px`, width: `${px}px`, minWidth: '20%' };
			}
			// flex-grow: 1 so assignedTo fills any remaining horizontal space (no right-side gap).
			return { flex: '1 0 30%', minWidth: '20%' };
		}
		if (id.startsWith('parents.')) {
			return { flex: '2 1 180px', minWidth: '120px' };
		}
		if (id === 'columnToHide') {
			return { display: 'none' };
		}
		if (leftColOverrideWidths[id] !== undefined) {
			// flex-grow: 0 so the column stays exactly at the user-set width and never grows back.
			const px = leftColOverrideWidths[id];
			return { flex: `0 0 ${px}px`, minWidth: `${px}px`, maxWidth: `${px}px` };
		}
		// Building and Location (apt/suite) columns get a small default cap; user can widen via drag
		const growFactor = listingWidthCols[id]?.width || 1;
		if (smallDefaultColIds.has(id)) {
			return { flex: `${growFactor} 1 0px`, minWidth: '60px', maxWidth: '150px' };
		}
		return { flex: `${growFactor} 1 0px`, minWidth: '60px' };
	}, [ listingWidthCols, leftColOverrideWidths, listingDataColumns, leftTableColumnsToHide, totalDataGrowFactors, smallDefaultColIds, ]);

	const filterRowColStyles = useCallback((column) => {
		return theadColStyles(column);
	}, [ theadColStyles, ]);

	const tbodyColStyles = useCallback((cell) => {
		const column = cell?.column || {};
		return theadColStyles(column);
	}, [ theadColStyles, ]);

	const theadColStylesForSystems = useCallback((column) => {
		const id = column?.id || '';
		if (id && id.startsWith('groups.')) {
			return {
				width:    `${systemColWidthPx}px`,
				minWidth: `${systemColWidthPx}px`,
				maxWidth: `${systemColWidthPx}px`,
			};
		}
		return {};
	}, [ systemColWidthPx, ]);

	const filterRowColStylesForSystems = useCallback((column) => {
		const id = column?.id || '';
		if (id && id.startsWith('groups.')) {
			return {
				width:    `${systemColWidthPx}px`,
				minWidth: `${systemColWidthPx}px`,
				maxWidth: `${systemColWidthPx}px`,
			};
		}
		return {};
	}, [ systemColWidthPx, ]);

	const tbodyColStylesForSystems = useCallback((cell) => {
		const id = cell?.column?.id || '';
		if (id && id.startsWith('groups.')) {
			return {
				width:    `${systemColWidthPx}px`,
				minWidth: `${systemColWidthPx}px`,
				maxWidth: `${systemColWidthPx}px`,
			};
		}
		return {};
	}, [ systemColWidthPx, ]);

	const isSystemsColumnResizable = useCallback((column) => {
		return (column?.id || '').startsWith('groups.');
	}, []);

	const cellOnClick = useCallback((cell, row) => {
		return (e) => {
			e.preventDefault();
			if (cell.column.id === 'checkbox') {
				toggleSelectSingleRow(cell.row.original.uuid, cell.row.index);
				setSelectedRowUUIDs((prev) => {
					const next = new Set(prev);
					if (next.has(cell.row.original.uuid)) {
						next.delete(cell.row.original.uuid);
					} else {
						next.add(cell.row.original.uuid);
					}
					return next;
				});
			} else if (cell.column.id.startsWith('groups.')) {
				const groupID = cell.column.id.split('.')[1];
				const group = listingGroups.filter((listingGroup) => {
					return (listingGroup.uuid === groupID);
				})[0];

				toggleGroup(group, row.index);
			} else {
				// set scroll position manually, not via function because of a race condition
				let scrollX = document.getElementById('table-scroll').scrollLeft;
				let scrollY = document.getElementById('table-scroll').scrollTop;

				let title = 'Edit Listings';

				if (listingConfigIsIntegration) {
					title = 'Listings Details';
				}

				app.openModal({
					scrollX,
					scrollY,
					modalSize:  2,
					showModal:  true,
					modalProps: {
						title,
						customClass:    'test',
						pendingChanges: app.hasPendingChanges(),
						jsx:            (
							<ListingsModal
								isEdit={true}
								listingConfigIsIntegration={listingConfigIsIntegration}
								listingFD={listingFD}
								pendingChanges={pendingChanges}
								listing={cell.row.original}
								listingToListingCollectionsMap={listingToListingCollectionsMap}
								listingToSystemsMap={listingToSystemsMap}
								findRootListings={findRootListings}
								updateListing={updateListing}
								accountUUID={accountUUID}
								systemGroupUUID={systemGroupUUID}
								buildingsDataArray={buildingsDataArray}

								listingConfigHasScheduling={listingConfigHasScheduling}
								activeListingConfig={activeListingConfig}
								setGroupValue={setGroupValue}
								listingGroups={listingGroups}
								getChildRelations={getChildRelations}
								getParentComponentName={getParentComponentName}
								listingCollectionHasListingGroupRelation={listingCollectionHasListingGroupRelation}
								listingConfigUUID={listingConfigUUID}
								parentsPrimaryColumnData={parentsPrimaryColumnData}
								listingConfigToListingsMap={listingConfigToListingsMap}
								fetchListingDataByListingConfig={fetchListingDataByListingConfig}
								removeListingsFromFD={removeListingsFromFD}
							/>
						),
					},
				});
			}
		};
	}, [ accountUUID, activeListingConfig, app, array, buildingsDataArray, fetchListingDataByListingConfig, removeListingsFromFD, findRootListings, getChildRelations, getParentComponentName, listingCollectionHasListingGroupRelation, listingConfigHasScheduling, listingConfigIsIntegration, listingConfigMap, listingConfigToListingsMap, listingConfigUUID, listingFD, listingGroups, listingToListingCollectionsMap, listingToSystemsMap, parentsPrimaryColumnData, pendingChanges, setGroupValue, systemGroupUUID, toggleGroup, toggleSelectSingleRow, updateListing, ]);

	let areAllRowsChecked = allRowsSelected;

	array.forEach((obj) => {
		if (!obj.isChecked) {
			areAllRowsChecked = false;
		}
	});

	const dataColumnMenuOptions = useMemo(() => {
		const ALWAYS_HIDDEN_IDS = new Set([ 'checkbox', 'columnToHide', 'assignedTo', 'scheduling', ]);
		return listingDataColumns
			.filter((col) => {
				if (!col.id) return false;
				if (ALWAYS_HIDDEN_IDS.has(col.id)) return false;
				const label = (typeof col.Header === 'string' ? col.Header : col.id);
				if (isIntegrationBookkeepingColumn(label)) return false;
				return true;
			})
			.map((col) => ({
				id:    col.id,
				label: typeof col.Header === 'string' ? col.Header : col.id,
			}));
	}, [ listingDataColumns, ]);

	const hasSystemsPane = isRootTier && listingSystemColumns.length > 0;

	if (validConfig) {
		return (
			<div ref={wrapperRef} style={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

				{/* Show/Hide column menu popover */}
				{showColumnMenu && (
					<div
						ref={columnMenuRef}
						className={style.slideshowColumnMenu}
						style={columnMenuStyle}
						onClick={(e) => e.stopPropagation()}
						data-test-id="listingColumnMenu"
					>
						<div className={style.slideshowColumnMenuTitle}>Columns</div>
						{dataColumnMenuOptions.map((option) => {
							const isHidden = hiddenLeftColIds.includes(option.id);
							return (
								<label key={option.id} className={style.slideshowColumnMenuItem} data-test-id={`listingColumnMenuItem--${option.id}`}>
									<input
										type="checkbox"
										checked={!isHidden}
										onChange={() => {
											setHiddenLeftColIds((prev) =>
												prev.includes(option.id)
													? prev.filter((id) => id !== option.id)
													: [ ...prev, option.id, ]
											);
										}}
										onClick={(e) => e.stopPropagation()}
									/>
									<span>{option.label}</span>
								</label>
							);
						})}
					</div>
				)}

			{/* Buttons bar — full width, above both panes */}
			<div className='secondaryNav topNav'>
				{secondaryNavButtons.map((button, i) => {
					return (
						<div
							key={`nav_btn_${i}`}
							className={button.className}
							onClick={button.onClick}
							data-test-id={button.dataTestId}
						>
							{button.icon ? <CustomIcon icon={button.icon}/> : ''}{button.text}
						</div>
					);
				})}
			</div>

			{/* Second toolbar row: Search (left) + Bulk Actions (middle) + Show/Hide (right) */}
			<div className={`secondaryNav ${style.slideshowToolbar}`}>
				<div className={style.slideshowSearchContainer}>
					<div className={style.slideshowTopSearch} onClick={(e) => e.stopPropagation()}>
						<CustomIcon icon="search"/>
						<input
							type="search"
							value={globalSearchQuery}
							onChange={(e) => setGlobalSearchQuery(e.target.value)}
							placeholder={`Search ${array.length} listings...`}
							aria-label="Search listings"
							onClick={(e) => e.stopPropagation()}
						/>
						{globalSearchQuery ? (
							<div className={'button'} onClick={(e) => { e.preventDefault(); setGlobalSearchQuery(''); }}>
								Clear
							</div>
						) : null}
					</div>
				</div>

				<div className={style.slideshowToolbarRight}>
					{selectedRowUUIDs.size > 0 && isRootTier && (
						<div className={style.bulkActionBar}>
							<div className={style.bulkActionCount}>{`${selectedRowUUIDs.size} item${selectedRowUUIDs.size > 1 ? 's' : ''} selected`}</div>
							<div className={style.bulkActionDivider} />
							<div style={{ display: 'flex', gap: '8px' }}>

								<div className={'button'} onClick={() => setShowBulkAssignWarning(true)}>
									Assign Selected
								</div>
								<div className={style.bulkActionDivider} />

								<div className={'button'} onClick={pushSelectedToAll}>
									Push to All
								</div>
								<div className={'button'} onClick={clearAllAssignments}>
									Clear All Assignments
								</div>
							</div>
							<div className={style.bulkActionDivider} />
							<div className={'button'} onClick={() => setSelectedRowUUIDs(new Set())}>
								Cancel
							</div>
						</div>
					)}
					{selectedRowUUIDs.size > 0 && !isRootTier && (
						<div className={style.bulkActionBar}>
							<div className={style.bulkActionCount}>{`${selectedRowUUIDs.size} item${selectedRowUUIDs.size > 1 ? 's' : ''} selected`}</div>
							<div className={style.bulkActionDivider} />
							<div className={'button'} onClick={() => setShowBulkAssignWarning(true)}>
								Assign Selected
							</div>
							<div className={style.bulkActionDivider} />
							<div className={'button'} onClick={() => setSelectedRowUUIDs(new Set())}>
								Cancel
							</div>
						</div>
					)}
					<div className={'button'} onClick={onShowHideButtonClick} data-test-id="listingColumnsBtn">
						<CustomIcon icon={'columns'} /> Show/Hide
					</div>
				</div>
			</div>

			<div style={hasSystemsPane ? { paddingRight: '1rem', marginTop: '0.5rem' } : { paddingRight: '1rem', marginTop: '0.5rem' }}>
			<div style={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
				<VultureTable
					style={style}
					columns={dataColumnsWrapped}
					data={visibleData}
					fd={listingFD}
					columnsToHide={leftTableColumnsToHide}
					secondaryNavButtons={[]}
					tertiaryNavButtons={[]}
					showSecondaryNav={false}
					page={page}
					totalResults={filteredRowsCount}
					onItemsPerPageChange={(newValue) => {
						setItemsPerPage(newValue);
						setPage(1);
					}}
					rowsPerPage={rowsPerPage}
					itemsPerPage={itemsPerPage}
					theadColStyles={theadColStyles}
					filterRowColStyles={filterRowColStyles}
					tbodyColStyles={tbodyColStyles}
					listingTable={true}
					listingConfigUUID={listingConfigUUID}
					listingConfigsWithSystems={listingConfigsWithSystems}
					allRowsSelected={areAllRowsChecked}
					toggleSelectAllRows={toggleSelectAllRows}
					showResultsAndRowsDropdown={false}
					hideFooter={true}
					hideFilterRow={true}
					cellOnClick={cellOnClick}
					onColumnResizeMouseDown={onLeftColHandleMouseDown}
					isColumnResizable={isLeftDataColumnResizable}
					hideHorizontalScrollbar={true}
					hideTopScroller={true}
					tableScrollStyle={tableBodyHeight ? { maxHeight: `${tableBodyHeight}px`, overflowY: 'auto' } : undefined}
					columnClassMap={columnClassMap}
				/>
			</div>

				{/* Shared footer: results + pagination + rows per page */}
				<div className={`secondaryNav ${style.sharedFooterControls}`}>
					<div className={style.resultsText}>
						{filteredRowsCount === 0
						? '0 results'
						: `${Math.min((page - 1) * itemsPerPage + 1, filteredRowsCount)}-${Math.min(page * itemsPerPage, filteredRowsCount)} of ${filteredRowsCount}`}
					</div>
					<div className={style.paginationContainer}>
						{tertiaryNavButtons.map((button, i) => {
							return (
								<div
									key={`footer_btn_${i}`}
									className={button.className}
									onClick={button.onClick}
								>
									{button.icon ? <CustomIcon icon={button.icon}/> : ''}{button.text}
								</div>
							);
						})}
					</div>
					<div className={style.rowsPerPageDropdown}>
						<select
							value={itemsPerPage}
							onChange={(e) => {
								setItemsPerPage(parseInt(e.target.value, 10));
								setPage(1);
							}}
						>
							{[ 5, 10, 25, 50, 100, ].map((option) => {
								return (
									<option key={option} value={option}>{option} per page</option>
								);
							})}
						</select>
					</div>
				</div>
			</div>

			{/* System Assignment Modal (Tier 1) */}

			{showBulkAssignWarning && (
				<div className={style.assignmentModalOverlay} onClick={() => setShowBulkAssignWarning(false)} style={{ zIndex: 9999 }}>
					<div className={style.assignmentModal} onClick={(e) => e.stopPropagation()}>
						<div className={style.assignmentModalHeader}>
							<div className={style.assignmentModalTitleGroup}>
								<span className={style.assignmentModalTitle}>Warning</span>
							</div>
							<span className={style.assignmentModalClose} onClick={() => setShowBulkAssignWarning(false)}>×</span>
						</div>
						<div className={style.assignmentModalSearch} style={{ padding: '16px' }}>
							{isRootTier
								? 'Choosing this method will override all existing system assignments.'
								: 'Choosing this method will override all existing parent listing assignments.'}
						</div>
						<div className={style.assignmentModalFooter}>
							<div className="button" onClick={() => { setShowBulkAssignWarning(false); setShowBulkAssignModal(true); }}>Proceed</div>
							<div className="button button-outlined" onClick={() => setShowBulkAssignWarning(false)}>Cancel</div>
						</div>
					</div>
				</div>
			)}
			{showBulkAssignModal && isRootTier && (
				<BulkListingAssignmentModal
					listingGroups={listingGroups}
					fd={fd}
					onSave={(uuids) => {
						saveBulkAssignments(uuids);
						setShowBulkAssignModal(false);
					}}
					onClose={() => setShowBulkAssignModal(false)}
				/>
			)}


			{showBulkAssignModal && !isRootTier && (
				<BulkParentListingAssignmentModal
					listingFD={listingFD}
					parentsPrimaryColumnData={parentsPrimaryColumnData}
					listingConfigToListingsMap={listingConfigToListingsMap}
					activeListingConfig={activeListingConfig}
					onSave={(parentM1Keys) => {
						saveBulkParentAssignments(parentM1Keys);
						setShowBulkAssignModal(false);
					}}
					onClose={() => setShowBulkAssignModal(false)}
				/>
			)}

			{assignmentModalListing && isRootTier && (
				<ListingAssignmentModal
					listing={assignmentModalListing}
					listingDataColumns={listingDataColumns}
					listingGroups={listingGroups}
					listingFD={listingFD}
					fd={fd}
					listingCollectionHasListingGroupRelation={listingCollectionHasListingGroupRelation}
					bulkToggleGroupsAndSave={bulkToggleGroupsAndSave}
					onClose={() => setAssignmentModalListing(null)}
				/>
			)}
			{/* Parent Listing Assignment Modal (Tier 2+) */}
			{parentAssignmentModalListing && !isRootTier && (
				<ParentListingAssignmentModal
					listing={parentAssignmentModalListing}
					listingDataColumns={listingDataColumns}
					listingFD={listingFD}
					parentsPrimaryColumnData={parentsPrimaryColumnData}
					listingConfigToListingsMap={listingConfigToListingsMap}
					activeListingConfig={activeListingConfig}
					app={app}
					onClose={() => setParentAssignmentModalListing(null)}
				/>
			)}
		</div>
	);
}

	return (
		<div>
			This is not a valid Listing Tier. Please return to Account Settings and add at least one data type to this Tier.
		</div>
	);
};

export default ListingsTable;